import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  FileText,
  CheckCircle2,
  Clock,
  Image as ImageIcon,
  Check,
  X,
  XCircle,
  Upload,
  Maximize2,
  MessageCircle,
  Star,
  AlertTriangle,
  ChevronLeft,
  Sparkles,
  Home,
  Layers,
  Ruler,
  Building2,
  MapPin,
  Palette,
} from "lucide-react";
import axiosInstance from "../../services/api/axios";
import { toast } from "react-toastify";
import { getMediaUrl, flattenPropertyConfigMediaForLeadUI } from "../../utils/media";
import { getPresignedDownloadUrls } from "../../utils/fileUtils";
import { normalizeMediaBySection, getSectionDisplayOrder } from "../../utils/designSubmissionMedia";
import DesignSubmissionModal from "../../components/DesignSubmissionModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { PrimaryButton, OutlineButton, CloseButton, DeleteButton } from "../../components/Buttons";
import LoadingImage from "../../components/LoadingImage";
import CountdownBadge from "../../components/CountdownBadge";
import LeadStatusTimeline from "../../components/InteriorDesigner/LeadStatusTimeline";
import DesignConversation from "../../components/interior/DesignConversation";
import DesignPhaseBadge from "../../components/interior/DesignPhaseBadge";
import { getDesignLead, acknowledgeLead, cancelLead, acknowledgeFeedback } from "../../services/interiorDesign";
import {
  hasStatus,
  ID_ACK_EXPIRED,
  ID_ITER_ACK_EXPIRED,
  ID_DELIVERY_EXPIRED,
} from "../../utils/idLeadStatus";

function getProjectPhaseIdFromLead(idLead) {
  if (!idLead) return null;
  const prop = idLead.property ?? idLead.Property;
  const phase = prop?.projectPhase ?? prop?.project_phase;
  return (
    phase?.projectPhaseId ??
    phase?.project_phase_id ??
    prop?.projectPhaseId ??
    null
  );
}

function extractFeaturedIterationId(item) {
  if (!item) return null;
  return (
    item.designIterationId ??
    item.design_iteration_id ??
    item.designIteration?.designIterationId ??
    item.designIteration?.id ??
    item.design_iteration?.design_iteration_id ??
    null
  );
}

const MAX_FEATURED_PER_PHASE = 8;

// Canonical status chip for a design iteration — derived from BOTH the buyer
// decision (iterationStatus) AND the developer decision (developerApprovalStatus).
// Mirrors the BV RequestDetails helper so the ID and BV screens read the same.
const getIterationStatusDisplay = (iter) => {
  const itStatus = (iter?.iterationStatus ?? iter?.iteration_status ?? "").toString().toUpperCase();
  const devStatus = (iter?.developerApprovalStatus ?? iter?.developer_approval_status ?? "").toString().toUpperCase();
  const hasFeedback = Boolean(
    (iter?.buyerFeedback ?? iter?.buyer_feedback) &&
      String(iter?.buyerFeedback ?? iter?.buyer_feedback).trim() !== ""
  );

  if (devStatus === "DEVELOPER_APPROVED") {
    return { key: "DEVELOPER_APPROVED", label: "Developer approved", tone: "emerald" };
  }
  if (devStatus === "DEVELOPER_REJECTED") {
    return { key: "DEVELOPER_REJECTED", label: "Developer rejected", tone: "rose" };
  }
  if (itStatus === "APPROVED") {
    return { key: "BUYER_APPROVED", label: "Buyer approved · developer pending", tone: "indigo" };
  }
  if (itStatus === "REJECTED") {
    return { key: "REJECTED", label: "Rejected", tone: "rose" };
  }
  if (itStatus === "FEEDBACK_PROVIDED" || hasFeedback) {
    return { key: "FEEDBACK", label: "Feedback sent", tone: "emerald" };
  }
  return { key: "AWAITING", label: "Awaiting feedback", tone: "amber" };
};

// Tailwind class clusters per tone so the pills stay visually consistent.
const STATUS_TONE_CLASSES = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
  indigo: "border-indigo-200 bg-indigo-50 text-indigo-700",
  amber: "border-amber-200 bg-amber-50 text-amber-700",
  rose: "border-rose-200 bg-rose-50 text-rose-700",
};

/** Same summary block as Designs.jsx featured confirmation */
function FeaturedBrowseConfirmDetails({ row }) {
  const idLead = row?.idLead ?? row?.id_lead;
  const leadRow = idLead;
  const prop = leadRow?.property ?? leadRow?.Property;
  const projectName =
    prop?.projectPhase?.project?.projectName ??
    prop?.project_phase?.project?.project_name ??
    "—";
  const propertyNo = prop?.propertyNo ?? prop?.property_no ?? "—";
  const configName =
    leadRow?.propertyConfig?.configName ??
    leadRow?.property_config?.config_name ??
    "—";
  const projectPhaseId = row?.projectPhaseId ?? row?.project_phase_id ?? null;

  const [currentFeatured, setCurrentFeatured] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(false);
  const [thumbUrls, setThumbUrls] = useState({});

  // Fetch the currently-featured iterations for this phase so the designer
  // sees what they're competing with (and the n/8 cap) before committing.
  useEffect(() => {
    if (!projectPhaseId) return undefined;
    let cancelled = false;
    setLoadingFeatured(true);
    (async () => {
      try {
        const res = await axiosInstance.get("/interior-designers/featured-iterations", {
          params: { projectPhaseId },
        });
        const body = res?.data ?? res;
        const items = Array.isArray(body?.items)
          ? body.items
          : Array.isArray(body)
            ? body
            : [];
        if (!cancelled) setCurrentFeatured(items);
      } catch (e) {
        if (!cancelled) setCurrentFeatured([]);
      } finally {
        if (!cancelled) setLoadingFeatured(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectPhaseId]);

  // Presign the first media s3_key from each featured iteration so we can
  // show a small thumb. Falls back to a gradient tile if unavailable.
  useEffect(() => {
    const keys = currentFeatured
      .map((item) => {
        const iter = item?.designIteration ?? item?.design_iteration;
        const media = Array.isArray(iter?.media) ? iter.media : [];
        const first = media[0];
        if (typeof first === "string") return first;
        return first?.s3_key ?? first?.s3Key ?? null;
      })
      .filter(Boolean);
    if (keys.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const urls = await getPresignedDownloadUrls(keys);
        if (!cancelled && urls && typeof urls === "object") {
          setThumbUrls(urls);
        }
      } catch (e) {
        // Silent — fallback tile renders.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentFeatured]);

  const featuredCount = currentFeatured.length;
  const remainingSlots = Math.max(0, MAX_FEATURED_PER_PHASE - featuredCount);
  const atCap = featuredCount >= MAX_FEATURED_PER_PHASE;

  const rows = [
    { label: "Design", value: row?.gridTitle || row?.title || "—" },
    { label: "Project", value: projectName },
    { label: "Property", value: propertyNo },
    { label: "Configuration", value: configName },
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 leading-relaxed">
        This iteration will be highlighted when buyers browse designs for this project phase. You can feature a
        different iteration later if needed.
      </p>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm space-y-2">
        {rows.map(({ label, value }) => (
          <div key={label} className="flex gap-2 min-w-0">
            <span className="text-gray-500 shrink-0 w-36">{label}</span>
            <span className="font-medium text-gray-900 break-words min-w-0">{value}</span>
          </div>
        ))}
      </div>
      {/* Currently-featured-in-this-phase strip — shows the cap (n/8) and
          the competing thumbnails so the designer can plan their picks
          instead of bumping the cap by surprise. */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Currently featured in this phase
          </span>
          <span
            className={`inline-flex items-center justify-center min-w-[44px] h-[20px] px-2 rounded-full text-[10px] font-bold tabular-nums ${
              atCap
                ? "bg-amber-100 text-amber-800 border border-amber-200"
                : "bg-slate-100 text-slate-700"
            }`}
          >
            {featuredCount}/{MAX_FEATURED_PER_PHASE}
          </span>
        </div>
        {loadingFeatured ? (
          <div className="flex gap-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="w-14 h-14 rounded-md bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : featuredCount === 0 ? (
          <p className="text-xs text-slate-500">No designs featured yet — this will be the first.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {currentFeatured.map((item) => {
              const iter = item?.designIteration ?? item?.design_iteration;
              const media = Array.isArray(iter?.media) ? iter.media : [];
              const first = media[0];
              const s3Key = typeof first === "string" ? first : first?.s3_key ?? first?.s3Key ?? null;
              const url = s3Key ? thumbUrls[s3Key] : null;
              const iterNum = iter?.iterationNumber ?? iter?.iteration_number;
              const label = iter?.iterationName?.trim?.() || `Iter ${iterNum ?? "?"}`;
              const featId = item?.featuredDesignIterationId ?? item?.featured_design_iteration_id ?? Math.random();
              return (
                <div
                  key={featId}
                  className="relative w-14 h-14 rounded-md overflow-hidden border border-slate-200 bg-gradient-to-br from-flamingo-50 to-slate-100"
                  title={label}
                >
                  {url ? (
                    <img
                      src={url}
                      alt={label}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-slate-500 px-1 text-center">
                      {label}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {atCap && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            <strong>At cap.</strong> You've filled all {MAX_FEATURED_PER_PHASE} slots. Remove an existing featured design from the Designs → Featured tab before adding a new one.
          </p>
        )}
        {!atCap && remainingSlots <= 2 && featuredCount > 0 && (
          <p className="text-xs text-slate-600">
            {remainingSlots === 1 ? "1 slot" : `${remainingSlots} slots`} remaining in this phase.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Amber review card – lead design request, iteration feedback, and related modals.
 * @param {"row"|"column"} bodyVariant – "row" matches inline lead/iteration toolbar; "column" stacks content for modals.
 */
function AmberReviewRequestPanel({
  className = "",
  title,
  description,
  headerBadge,
  children,
  actions,
  bodyVariant = "row",
}) {
  const bodyClass =
    bodyVariant === "column"
      ? "p-4 space-y-4 flex flex-col gap-4"
      : "p-2 sm:p-4 space-y-4 flex flex-wrap items-center justify-between gap-2";

  return (
    <div
      className={`rounded-xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-sm overflow-hidden ${className}`.trim()}
    >
      <div className="px-4 py-3 border-b border-amber-200/60 bg-amber-100/70">
        <div className="flex items-center gap-2 flex-wrap">
          <Clock className="text-amber-600 flex-shrink-0" size={18} />
          <h2 className="text-sm font-medium text-amber-900">{title}</h2>
          {headerBadge}
        </div>
        {description != null && description !== "" ? (
          typeof description === "string" ? (
            <p className="text-sm text-amber-800/90 mt-1">{description}</p>
          ) : (
            <div className="mt-1">{description}</div>
          )
        ) : null}
      </div>
      <div className={bodyClass}>
        {children}
        {actions ? (
          <div
            className={`flex flex-wrap gap-3 pt-2 ${bodyVariant === "column" ? "justify-end w-full mt-auto" : ""}`}
          >
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  );
}

const LeadDetails = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(null);
  const [designSubmission, setDesignSubmission] = useState(null);
  const [designSubmissions, setDesignSubmissions] = useState([]);
  // Simplified design conversation (derived thread + collapsed phase) for the designer's view.
  const [conversation, setConversation] = useState(null);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [loadingDesigns, setLoadingDesigns] = useState(false);
  const [selectedIterationId, setSelectedIterationId] = useState(null);
  const [selectedIterationData, setSelectedIterationData] = useState(null);
  const [selectedSubmissionData, setSelectedSubmissionData] = useState(null);
  const [configImages, setConfigImages] = useState([]);
  const [approving, setApproving] = useState(false);
  const [showDesignModal, setShowDesignModal] = useState(false);
  const [designModalStep, setDesignModalStep] = useState(0);
  const [hasCheckedApproval, setHasCheckedApproval] = useState(false);
  const [iterationUploadSubmission, setIterationUploadSubmission] = useState(null);

  // Inline iteration feedback review (Accept/Decline)
  const [acknowledgeRejectNote, setAcknowledgeRejectNote] = useState("");
  const [acknowledging, setAcknowledging] = useState(false);
  // Confirmation modals for Accept / Decline
  const [showAcceptConfirmModal, setShowAcceptConfirmModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineNote, setDeclineNote] = useState("");

  /** Featured-for-browse confirmation (same flow as Designs.jsx) */
  const [featuredConfirmRow, setFeaturedConfirmRow] = useState(null);
  const [isFeaturingIteration, setIsFeaturingIteration] = useState(false);

  // Inbox: primary layout is interactions-style; sidebar switches Design submission vs Interactions.
  const [inboxView, setInboxView] = useState("design_submission");
  const [, setHasAutoSelected] = useState(false);

  // Requirements modal (shows HTML-rendered requirements)
  const [showRequirementsModal, setShowRequirementsModal] = useState(false);
  const [notesModalContent, setNotesModalContent] = useState("");
  const [notesModalTitle, setNotesModalTitle] = useState("Designer Notes");

  // Carousel indices for config, portfolio, and design iteration images
  const [configCarouselIndex, setConfigCarouselIndex] = useState(0);
  const [portfolioCarouselIndex, setPortfolioCarouselIndex] = useState(0);
  const [designCarouselIndex, setDesignCarouselIndex] = useState(0);

  // Map of s3_key -> presigned URL for portfolio images
  const [portfolioImageUrls, setPortfolioImageUrls] = useState({});
  // Presigned URLs for the buyer's lead reference images (lead.media), keyed
  // by s3_key. New leads store S3 keys; legacy leads may hold base64 strings
  // (rendered directly without a presign).
  const [leadMediaUrls, setLeadMediaUrls] = useState({});

  // Presign the buyer's lead reference images (lead.media) so the ID can view
  // them via S3 download URLs. Only S3-keyed entries are presigned; legacy
  // base64 leads (where media is a raw string) are intentionally not shown.
  useEffect(() => {
    const media = Array.isArray(lead?.media) ? lead.media : [];
    const keys = media
      .map((m) => (typeof m === "string" ? null : (m?.s3_key ?? m?.s3Key ?? m?.key)))
      .filter(Boolean);
    if (keys.length === 0) {
      setLeadMediaUrls({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const urls = await getPresignedDownloadUrls(keys);
        if (!cancelled && urls && typeof urls === "object") setLeadMediaUrls(urls);
      } catch (e) {
        // Silent — the section just won't render thumbnails.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.media]);
  // Map of s3_key -> presigned URL for design iteration media (Design Images)
  const [designIterationImageUrls, setDesignIterationImageUrls] = useState({});
  const openNotesModal = (content, title = "Designer Notes") => {
    if (!content || !String(content).trim()) return;
    setNotesModalTitle(title);
    setNotesModalContent(String(content));
  };

  const fetchDesignSubmissions = async (idLeadId, options = {}) => {
    if (!idLeadId) return;

    try {
      setLoadingDesigns(true);
      const lead = await getDesignLead(idLeadId);
      // Adapt the flat lead + deliverables[] to the legacy {designSubmission, designIterations}
      // wrapper shape (one pseudo-submission per lead). `iterationStatus` aliases the
      // deliverable's single `status` so the legacy grid keeps working.
      const deliverables = (lead?.deliverables || []).map((d) => ({ ...d, iterationStatus: d.status }));
      const wrapper = {
        designSubmission: {
          designSubmissionId: idLeadId,
          title: lead?.title,
          description: lead?.description,
          idLead: lead,
        },
        designIterations: deliverables,
      };
      const submissionsArray = deliverables.length ? [wrapper] : [];

      setDesignSubmissions(submissionsArray);
      setDesignSubmission(deliverables.length ? wrapper.designSubmission : null);

      // Reset auto-select when new data is loaded
      setHasAutoSelected(false);

      // When deliverables exist, open Interactions by default; otherwise Design submission.
      setInboxView(deliverables.length ? "interactions" : "design_submission");

      const selectLatestForSubmissionId = options.selectLatestForSubmissionId;
      if (selectLatestForSubmissionId) {
        const wrapper = submissionsArray.find((sw) => {
          const sub = sw?.designSubmission || sw;
          const id = sub?.designSubmissionId || sub?.id;
          return id && parseInt(id) === parseInt(selectLatestForSubmissionId);
        });

        const submission = wrapper?.designSubmission || wrapper || null;
        const iterations = wrapper?.designIterations || [];
        if (submission && iterations.length > 0) {
          const sorted = [...iterations].sort((a, b) => {
            const aNum = a.iterationNumber || 0;
            const bNum = b.iterationNumber || 0;
            if (aNum !== bNum) return bNum - aNum;
            const aDate = new Date(a.createdDate || 0).getTime();
            const bDate = new Date(b.createdDate || 0).getTime();
            return bDate - aDate;
          });
          const latest = sorted[0];
          setSelectedIterationId(latest.designIterationId || latest.id);
          setSelectedIterationData(latest);
          setSelectedSubmissionData(submission);
        }
      }
    } catch (error) {
      console.error("Error fetching design submissions:", error);
      toast.error("Failed to fetch design submissions");
      setDesignSubmissions([]);
      setDesignSubmission(null);
    } finally {
      setLoadingDesigns(false);
    }
  };

  // Load the derived design conversation (thread + collapsed phase) for this lead. Reloads
  // whenever the submissions refresh so the thread never drifts from the underlying data.
  const fetchConversation = async (idLeadId) => {
    if (!idLeadId) return;
    setLoadingConversation(true);
    try {
      const data = await getDesignLead(idLeadId);
      setConversation(data);
    } catch (error) {
      setConversation(null);
    } finally {
      setLoadingConversation(false);
    }
  };

  useEffect(() => {
    const idLeadId = lead?.interiorDesignerLeadId;
    if (idLeadId) fetchConversation(idLeadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.interiorDesignerLeadId, designSubmissions]);

  const openUploadUpdatedDesign = () => {
    const submissionId = selectedSubmission?.designSubmissionId || selectedSubmission?.id;
    if (!submissionId) {
      toast.error("Design submission not found for this iteration");
      return;
    }

    setIterationUploadSubmission({
      designSubmissionId: submissionId,
      title: selectedSubmission?.title || "Design Submission",
      description: selectedSubmission?.description || "",
    });
    setDesignModalStep(0);
    setShowDesignModal(true);
  };

  const openEditBoq = () => {
    if (!selectedSubmission) {
      toast.error("Design submission not found");
      return;
    }
    setDesignModalStep(1);
    setShowDesignModal(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Check if lead was passed via navigation state
        const passedLead = routerLocation.state?.lead;
        let currentLead = null;

        if (passedLead) {
          currentLead = passedLead;
          setLead(currentLead);
        } else if (leadId) {
          // Fetch all leads and find the one matching leadId
          const response = await axiosInstance.get(`/interior-designers/leads`);

          let leadsArray = [];
          if (Array.isArray(response)) {
            leadsArray = response;
          } else if (Array.isArray(response?.items)) {
            leadsArray = response.items;
          } else if (Array.isArray(response?.data?.items)) {
            leadsArray = response.data.items;
          } else if (Array.isArray(response?.data)) {
            leadsArray = response.data;
          }

          // Find lead matching the ID
          const foundLead = leadsArray.find(
            (l) => l.interiorDesignerLeadId === parseInt(leadId)
          );

          if (foundLead) {
            currentLead = foundLead;
            console.log("LeadRequestDetails: Found lead in leads array:", currentLead);
            if (currentLead.interiorDesignerPortfolioItems) {
              console.log("LeadRequestDetails: Portfolio items:", currentLead.interiorDesignerPortfolioItems);
              console.log("LeadRequestDetails: First portfolio item ID candidates:", {
                portfolioItemId: currentLead.interiorDesignerPortfolioItems[0]?.portfolioItemId,
                interiorDesignerPortfolioItemId: currentLead.interiorDesignerPortfolioItems[0]?.interiorDesignerPortfolioItemId,
                id: currentLead.interiorDesignerPortfolioItems[0]?.id
              });
            }
            setLead(currentLead);
          }
        }

        if (currentLead) {
          if (!passedLead) {
            console.log("LeadRequestDetails: currentLead after fetch:", currentLead);
          }
          console.log("Current lead:", currentLead);
          console.log("Lead property:", currentLead?.property);
          console.log("Lead propertyConfig:", currentLead?.property?.propertyConfig);

          // Fetch design submissions with iterations
          await fetchDesignSubmissions(currentLead.interiorDesignerLeadId);


          if (!currentLead.isIdAcknowledged && !hasCheckedApproval) {
            setHasCheckedApproval(true);
          }

          // Fetch config images and files
          // Extract config the same way as in render
          const property = currentLead?.property || {};
          const config = currentLead?.propertyConfig || {};

          // Try multiple possible field names for propertyConfigId
          const propertyConfigId =
            config?.propertyConfigId ||
            config?.id ||
            property?.propertyConfigId;

          console.log("Property Config ID:", propertyConfigId);
          console.log("Config object:", config);
          console.log("Config keys:", config ? Object.keys(config) : "No config");
          console.log("Full property object:", property);

          // Always try to fetch config media
          if (propertyConfigId) {
            console.log("✅ Property Config ID found:", propertyConfigId);
            console.log("Making API call to /property/config/media with property_config_id:", propertyConfigId);
            try {
              const imagesResponse = await axiosInstance.get(
                "/property/config/media",
                {
                  params: {
                    property_config_id: propertyConfigId,
                  },
                }
              );
              console.log("✅ API call successful, response received:", imagesResponse);
              setConfigImages(flattenPropertyConfigMediaForLeadUI(imagesResponse));
            } catch (error) {
              console.error("Error fetching config media:", error);
              console.error("Error details:", error.response?.data || error.message);
              console.error("Error status:", error.response?.status);
              setConfigImages([]);
            }
          } else {
            console.warn("❌ Property Config ID not found, cannot fetch config media");
            console.warn("Config object:", config);
            console.warn("Property object:", property);
            console.warn("Available paths checked:");
            console.warn("  - config?.propertyConfigId:", config?.propertyConfigId);
            console.warn("  - config?.id:", config?.id);
            console.warn("  - property?.propertyConfigId:", property?.propertyConfigId);
            console.warn("  - currentLead?.property?.propertyConfig?.propertyConfigId:", currentLead?.property?.propertyConfig?.propertyConfigId);

            // Try with a test ID to verify API works (remove after debugging)
            // Uncomment below to test if API endpoint works at all
            // console.log("Testing API with property_config_id=1...");
            // try {
            //   const testResponse = await axiosInstance.get("/property/config/media", {
            //     params: { property_config_id: 1 },
            //   });
            //   console.log("Test API call successful:", testResponse);
            // } catch (testError) {
            //   console.error("Test API call failed:", testError);
            // }
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch lead details");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [leadId, routerLocation.state, hasCheckedApproval]);

  // Separate useEffect to fetch config media when lead is available
  useEffect(() => {
    const fetchConfigMedia = async () => {
      if (!lead) {
        console.log("No lead available yet, skipping config media fetch");
        return;
      }

      const property = lead?.property || {};
      const config = property?.propertyConfig || {};

      const propertyConfigId =
        config?.propertyConfigId ||
        config?.id ||
        property?.propertyConfigId;

      console.log("=== Fetching Config Media ===");
      console.log("Lead:", lead);
      console.log("Property:", property);
      console.log("Config:", config);
      console.log("Property Config ID:", propertyConfigId);

      if (!propertyConfigId) {
        console.warn("⚠️ Property Config ID not found. Cannot fetch config media.");
        console.warn("Config object keys:", config ? Object.keys(config) : "No config");
        return;
      }

      try {
        console.log("🚀 Making API call to /property/config/media");
        console.log("Request params: { property_config_id:", propertyConfigId, "}");

        const imagesResponse = await axiosInstance.get(
          "/property/config/media",
          {
            params: {
              property_config_id: propertyConfigId,
            },
          }
        );

        console.log("✅ API Response received:", imagesResponse);
        setConfigImages(flattenPropertyConfigMediaForLeadUI(imagesResponse));
      } catch (error) {
        console.error("❌ Error fetching config media:", error);
        console.error("Error response:", error.response);
        console.error("Error message:", error.message);
        setConfigImages([]);
      }
    };

    fetchConfigMedia();
  }, [lead]);

  useEffect(() => {
    setConfigCarouselIndex(0);
    setPortfolioCarouselIndex(0);
    setDesignCarouselIndex(0);
  }, [lead?.interiorDesignerLeadId, selectedIterationId, inboxView]);

  // Fetch presigned URLs for portfolio images with s3_key
  useEffect(() => {
    const fetchPortfolioImageUrls = async () => {
      const portfolioItems = lead?.interiorDesignerPortfolioItems || [];
      const imagesWithS3Key = [];
      portfolioItems.forEach((item) => {
        const imgs = item.images ? (Array.isArray(item.images) ? item.images : Object.values(item.images || {})) : [];
        imgs.forEach((img) => {
          const s3Key = img?.s3_key || img?.s3Key || img?.key;
          if (img && typeof img === "object" && s3Key && !img.url && !img.downloadUrl) {
            imagesWithS3Key.push(img);
          }
        });
      });
      if (imagesWithS3Key.length === 0) {
        setPortfolioImageUrls({});
        return;
      }
      try {
        const s3Keys = imagesWithS3Key.map((img) => img.s3_key || img.s3Key || img.key);
        const uniqueS3Keys = [...new Set(s3Keys)];
        const presignedUrls = await getPresignedDownloadUrls(uniqueS3Keys, axiosInstance);
        const urlMap = {};
        presignedUrls.forEach((presigned, index) => {
          const s3Key = uniqueS3Keys[index];
          const downloadUrl = presigned.download_url || presigned.downloadUrl || presigned.url;
          if (downloadUrl) urlMap[s3Key] = downloadUrl;
        });
        setPortfolioImageUrls(urlMap);
      } catch (error) {
        console.error("Error fetching presigned URLs for portfolio images:", error);
        setPortfolioImageUrls({});
      }
    };
    if (lead && !loading) {
      fetchPortfolioImageUrls();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.interiorDesignerPortfolioItems, lead?.interiorDesignerLeadId, loading]);

  // Fetch presigned URLs for design iteration media (Design Images in each iteration)
  useEffect(() => {
    const fetchDesignIterationImageUrls = async () => {
      const s3Keys = [];
      (designSubmissions || []).forEach((submissionWrapper) => {
        const iterations = submissionWrapper?.designIterations || [];
        iterations.forEach((iteration) => {
          const media = iteration?.media;
          if (!media) return;
          const bySection = normalizeMediaBySection(media);
          Object.values(bySection || {}).flat().forEach((item) => {
            const key = item?.s3_key || item?.s3Key || item?.key;
            if (!key || typeof key !== "string" || item?.url || item?.downloadUrl) return;
            // Skip base64 strings (design iteration media can be s3_key or legacy base64)
            if (key.startsWith("/9j/") || key.startsWith("iVBORw0KGgo") || key.startsWith("data:")) return;
            s3Keys.push(key);
          });
        });
      });
      const uniqueS3Keys = [...new Set(s3Keys)];
      if (uniqueS3Keys.length === 0) {
        setDesignIterationImageUrls({});
        return;
      }
      try {
        const presignedUrls = await getPresignedDownloadUrls(uniqueS3Keys, axiosInstance);
        const urlMap = {};
        presignedUrls.forEach((presigned, index) => {
          const s3Key = uniqueS3Keys[index];
          const downloadUrl = presigned.download_url || presigned.downloadUrl || presigned.url;
          if (downloadUrl) urlMap[s3Key] = downloadUrl;
        });
        setDesignIterationImageUrls(urlMap);
      } catch (error) {
        console.error("Error fetching presigned URLs for design iteration images:", error);
        setDesignIterationImageUrls({});
      }
    };
    if (designSubmissions?.length) {
      fetchDesignIterationImageUrls();
    } else {
      setDesignIterationImageUrls({});
    }
  }, [designSubmissions]);

  const handleBack = () => {
    navigate("/interior-designer/leads");
  };

  const handleApprove = async () => {
    if (!lead?.interiorDesignerLeadId) return;

    try {
      setApproving(true);
      // Proper state-machine endpoint: persists is_id_acknowledged AND advances the lead
      // status to ACKNOWLEDGED, returning the flat detail. The legacy PUT /leads/{id}
      // silently dropped is_id_acknowledged (not a RequestIDLeadUpdate field), so the
      // acknowledgement never stuck and the badge reverted to "Pending acknowledgement".
      const detail = await acknowledgeLead(lead.interiorDesignerLeadId);
      setLead((prev) => ({ ...prev, ...(detail || {}) }));
      // Refresh the derived thread so it reflects ACKNOWLEDGED.
      fetchConversation(lead.interiorDesignerLeadId);

      toast.success("Design request acknowledged");
    } catch (error) {
      console.error("Error acknowledging lead:", error);
      toast.error("Failed to acknowledge request");
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!lead?.interiorDesignerLeadId) return;

    try {
      setApproving(true);
      // Proper state-machine endpoint (CANCELLED). The legacy PUT with lead_status:"LOST"
      // was silently dropped by RequestIDLeadUpdate, so the reject never took effect.
      const detail = await cancelLead(lead.interiorDesignerLeadId);
      setLead((prev) => ({ ...prev, ...(detail || {}) }));

      toast.success("Request declined");
      // Navigate back after rejection
      setTimeout(() => {
        navigate("/interior-designer/leads");
      }, 1500);
    } catch (error) {
      console.error("Error rejecting lead:", error);
      toast.error("Failed to decline request");
    } finally {
      setApproving(false);
    }
  };

  const refreshLeadFromApi = useCallback(async () => {
    if (!leadId) return;
    try {
      const response = await axiosInstance.get(`/interior-designers/leads`);
      let leadsArray = [];
      if (Array.isArray(response)) {
        leadsArray = response;
      } else if (Array.isArray(response?.items)) {
        leadsArray = response.items;
      } else if (Array.isArray(response?.data?.items)) {
        leadsArray = response.data.items;
      } else if (Array.isArray(response?.data)) {
        leadsArray = response.data;
      }
      const foundLead = leadsArray.find((l) => l.interiorDesignerLeadId === parseInt(leadId, 10));
      if (foundLead) setLead(foundLead);
    } catch (e) {
      console.error("Error refreshing lead:", e);
    }
  }, [leadId]);

  const handleFeaturedConfirmClose = useCallback(() => {
    if (!isFeaturingIteration) setFeaturedConfirmRow(null);
  }, [isFeaturingIteration]);

  const handleFeaturedConfirmSubmit = useCallback(async () => {
    const row = featuredConfirmRow;
    if (!row) return;
    const projectPhaseId = row.projectPhaseId;
    const designIterationId = row.designIterationId;
    if (projectPhaseId == null || projectPhaseId === "" || designIterationId == null) {
      toast.warn("Cannot feature: missing project phase or iteration.");
      setFeaturedConfirmRow(null);
      return;
    }
    setIsFeaturingIteration(true);
    try {
      await axiosInstance.post("/interior-designers/featured-iterations", {
        projectPhaseId,
        designIterationIds: [Number(designIterationId)],
        propertyConfigCosts: [],
      });
      toast.success("Iteration is now featured for browse.");
      setFeaturedConfirmRow(null);
      await refreshLeadFromApi();
    } catch (error) {
      console.error("Error featuring iteration:", error);
      toast.error(error.response?.data?.message || "Failed to set as featured.");
    } finally {
      setIsFeaturingIteration(false);
    }
  }, [featuredConfirmRow, refreshLeadFromApi]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatRelativeDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Recent";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return "Last week";
    if (diffDays < 14) return "2 weeks ago";
    return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  const buildIterationUpdatePayload = (iteration, overrides) => {
    const payload = {};
    const keysToPreserve = [
      "estimatedPrice",
      "media",
      "designerNotes",
      "buyerFeedback",
      "isFinal",
      "isIdAcknowledged",
      "iterationStatus",
      "developerApprovalStatus",
      "developerApprovalNotes",
    ];
    keysToPreserve.forEach((key) => {
      if (iteration && iteration[key] !== undefined) {
        payload[key] = iteration[key];
      }
    });
    return { ...payload, ...overrides };
  };

  // Helper function to get image source (base64 support, S3 keys, presigned URLs)
  const getImageSrc = (image) => {
    if (!image) return null;

    // Handle object with s3_key/s3Key/key (S3 storage) - use presigned URL if fetched
    if (typeof image === "object") {
      const s3Key = image?.s3_key || image?.s3Key || image?.key;
      if (s3Key) {
        if (portfolioImageUrls[s3Key]) return portfolioImageUrls[s3Key];
        if (designIterationImageUrls[s3Key]) return designIterationImageUrls[s3Key];
        if (image.url || image.downloadUrl) return getMediaUrl(image) || image.url || image.downloadUrl;
        return null; // Will show once presigned URL is fetched
      }

      // Handle presigned URL from API
      if (image.url || image.downloadUrl) {
        return getMediaUrl(image) || image.url || image.downloadUrl;
      }

      // Handle base64
      if (image.base64) {
        const base64String = image.base64;
        if (base64String.startsWith("/9j/")) {
          return `data:image/jpeg;base64,${base64String}`;
        } else if (base64String.startsWith("iVBORw0KGgo")) {
          return `data:image/png;base64,${base64String}`;
        } else if (base64String.startsWith("data:")) {
          return base64String;
        } else {
          return `data:image/jpeg;base64,${base64String}`;
        }
      }
    }

    // Handle string format
    if (typeof image === "string") {
      // Check if it's a base64 string
      if (image.startsWith("data:image/") || image.startsWith("/9j/") || image.length > 100) {
        if (image.startsWith("/9j/")) {
          return `data:image/jpeg;base64,${image}`;
        } else if (image.startsWith("iVBORw0KGgo")) {
          return `data:image/png;base64,${image}`;
        } else if (image.startsWith("data:")) {
          return image;
        } else {
          return `data:image/jpeg;base64,${image}`;
        }
      } else {
        // Assume it's an S3 key or URL
        return getMediaUrl({ key: image });
      }
    }

    return null;
  };

  // Flatten iterations from all submissions to create the requests list (inbox).
  // API shape: designSubmissions = [{ designSubmission: {...}, designIterations: [...] }].
  // Each iteration: designIterationId, buyerFeedback, isIdAcknowledged, idAcknowledgedDate, iterationNumber, estimatedPrice, designerNotes, media, etc.
  const requests = designSubmissions
    .flatMap((submissionWrapper) => {
      const submission = submissionWrapper.designSubmission || submissionWrapper;
      const iterations = submissionWrapper.designIterations || [];

      return iterations.map((iteration) => ({
        id: iteration.designIterationId || iteration.id,
        iterationNumber: iteration.iterationNumber || 1,
        title: submission.title || "Design Submission",
        description: submission.description || "",
        createdDate: iteration.createdDate || submission.createdDate,
        submission: submission,
        iteration: iteration,
      }));
    })
    .sort((a, b) => {
      const ta = a?.createdDate ? new Date(a.createdDate).getTime() : 0;
      const tb = b?.createdDate ? new Date(b.createdDate).getTime() : 0;
      const safeA = Number.isFinite(ta) ? ta : 0;
      const safeB = Number.isFinite(tb) ? tb : 0;
      if (safeB !== safeA) return safeB - safeA;
      return (b?.iterationNumber || 0) - (a?.iterationNumber || 0);
    });

  // Latest iteration id per submission. The ID can only acknowledge / act on
  // the latest iteration (mirrors the backend supersession 409). Older
  // iterations are frozen once a newer one exists.
  const latestIterationIds = (() => {
    const ids = new Set();
    (designSubmissions || []).forEach((sw) => {
      const iterations = sw.designIterations || [];
      let best = null;
      iterations.forEach((it) => {
        const n = it.iterationNumber || 0;
        if (best === null || n > (best.iterationNumber || 0)) best = it;
      });
      if (best) ids.add(best.designIterationId || best.id);
    });
    return ids;
  })();

  const selectedIteration =
    selectedIterationData ||
    (selectedIterationId ? requests.find((req) => req.id === selectedIterationId)?.iteration : null);

  const selectedSubmission =
    selectedSubmissionData ||
    (selectedIteration ? requests.find((req) => req.id === selectedIterationId)?.submission : null);

  // Only the latest iteration of a submission is actionable by the ID.
  const isSelectedIterationLatest =
    selectedIterationId != null && latestIterationIds.has(selectedIterationId);

  const selectedIterationName =
    selectedIteration?.interactionName?.trim?.() ||
    selectedIteration?.interaction_name?.trim?.() ||
    selectedIteration?.iterationName?.trim?.() ||
    selectedIteration?.iteration_name?.trim?.() ||
    selectedIteration?.title?.trim?.() ||
    (selectedIteration?.iterationNumber ? `Iteration #${selectedIteration.iterationNumber}` : "");

  // Unread = buyer sent feedback and designer hasn't acknowledged this iteration yet
  const isUnreadRequest = (request) =>
    Boolean(
      request?.iteration?.buyerFeedback &&
      request?.iteration?.isIdAcknowledged === false
    );

  // Auto-select first iteration/request when data loads so detail + inline approval banner show
  useEffect(() => {
    if (!loadingDesigns && requests.length > 0 && inboxView === "interactions") {
      if (!selectedIterationId && !selectedIterationData) {
        const firstRequest = requests[0];
        if (!firstRequest) return;
        setSelectedIterationId(firstRequest.id);
        setSelectedIterationData(firstRequest.iteration);
        setSelectedSubmissionData(firstRequest.submission);
        setHasAutoSelected(true);
      }
    }
  }, [requests, loadingDesigns, inboxView, selectedIterationId, selectedIterationData]);

  // Keep selection in sync with requests (e.g. after acknowledge refetch)
  useEffect(() => {
    if (selectedIterationId && requests.length > 0) {
      const req = requests.find((r) => r.id === selectedIterationId);
      if (req) {
        setSelectedIterationData(req.iteration);
        setSelectedSubmissionData(req.submission);
      }
    }
  }, [requests, selectedIterationId]);

  const submitAcknowledge = async (action, rejectNoteOverride) => {
    const iterationId = selectedIterationId;
    const iteration = selectedIterationData;
    if (!iterationId || !iteration) {
      toast.error("Iteration not found");
      return;
    }
    const note = rejectNoteOverride !== undefined ? rejectNoteOverride : acknowledgeRejectNote;
    if (action === "reject" && !(note && note.trim())) {
      toast.error("Please provide a description before declining.");
      return;
    }

    try {
      setAcknowledging(true);
      // The designer acknowledges the change request on the lead's latest deliverable.
      await acknowledgeFeedback(lead.interiorDesignerLeadId);

      toast.success(
        action === "accept"
          ? "Request accepted"
          : "Declined and marked as read"
      );
      setAcknowledgeRejectNote("");
      setDeclineNote("");
      setShowAcceptConfirmModal(false);
      setShowDeclineModal(false);
      if (lead?.interiorDesignerLeadId) {
        // Refresh both the iteration list AND the lead itself — the lead
        // carries status / hasNewRequest / deadlines that just changed.
        await Promise.all([
          fetchDesignSubmissions(lead.interiorDesignerLeadId),
          refreshLeadFromApi(),
        ]);
      }
    } catch (error) {
      console.error("Error acknowledging iteration:", error);
      toast.error(
        error?.response?.data?.message || "Failed to update request"
      );
    } finally {
      setAcknowledging(false);
    }
  };

  // Skeleton Loader (single column – layout uses sidebar + main after load)
  const SkeletonLoader = () => (
    <div className="flex flex-col min-h-screen font-['Gotham',sans-serif] font-light antialiased">
      <div className="h-14 border-b border-gray-200 bg-white animate-pulse" />
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-4">
          <div className="h-8 w-64 bg-gray-200 rounded mb-4 animate-pulse" />
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <SkeletonLoader />;
  }

  if (!lead) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-8 font-['Gotham',sans-serif] font-light antialiased">
        {/* Breadcrumb */}
        <nav className="w-full mb-6" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-1 text-gray-500 text-sm">
            <li>
              <button
                className="hover:text-flamingo-600 transition-colors"
                onClick={handleBack}
                type="button"
              >
                Leads
              </button>
            </li>
            <li>
              <span className="mx-1 text-gray-400">/</span>
            </li>
            <li className="text-gray-700 font-medium">Details</li>
          </ol>
        </nav>
        <div className="text-red-600 mb-4 text-xl font-medium">Lead not found</div>
        <button
          onClick={handleBack}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Back to Leads
        </button>
      </div>
    );
  }

  const property = lead?.property || {};
  const config = property?.propertyConfig || lead?.propertyConfig || {};

  const unitNo = property.propertyNo || "N/A";

  const configType = config.sizeBhk ? `${config.sizeBhk} BHK` : "N/A";
  const area = config.carpetAreaSqft
    ? `${config.carpetAreaSqft} sq.ft`
    : config.carpetAreaSqmt
      ? `${config.carpetAreaSqmt} sq.mt`
      : "N/A";

  const towerAndFloor = property.buildingNo
    ? ` ${property.buildingNo}${property.floorNo ? `, ${property.floorNo} Floor` : ""}`
    : property.floorNo
      ? `${property.floorNo} Floor`
      : "N/A";

  const configName = config.configName || configType || "N/A";

  // Hero band context (BV RequestDetails parity). The ID lead does not expose
  // the buyer's personal name (designer-facing view), so the subtitle is built
  // from the project / designer / unit that the screen already derives.
  const heroProject = property?.projectPhase?.project || property?.project_phase?.project || {};
  const heroProjectName = heroProject?.projectName || heroProject?.project_name || "";
  const heroAddress = heroProject?.address || {};
  const heroLocation =
    heroAddress?.city && heroAddress?.state
      ? `${heroProjectName ? `${heroProjectName}, ` : ""}${heroAddress.city}, ${heroAddress.state}`.trim()
      : heroProjectName;
  const heroDesignerName =
    lead?.interiorDesignerPortfolioItems?.[0]?.interiorDesigner?.name ||
    lead?.interiorDesigner?.name ||
    "";

  const embeddedIdLead = selectedSubmission?.idLead || selectedSubmission?.id_lead || null;
  // Prefer whichever source actually has data. `[] || x` evaluates to `[]` in JS
  // (arrays are truthy), so we explicitly check length before falling back.
  const leadFeatured =
    lead?.featuredDesignIterations || lead?.featured_design_iterations || [];
  const embeddedFeatured =
    embeddedIdLead?.featuredDesignIterations ||
    embeddedIdLead?.featured_design_iterations ||
    [];
  const featuredIterations =
    leadFeatured.length > 0 ? leadFeatured : embeddedFeatured;
  const featuredDesign = featuredIterations[0];
  const featuredIteration = featuredDesign?.designIteration || featuredDesign?.design_iteration || null;
  const isFeaturedRequest = Boolean(featuredIterations.length && featuredIteration);
  const browseProjectPhaseId =
    getProjectPhaseIdFromLead(lead) || getProjectPhaseIdFromLead(embeddedIdLead);
  const selectedIterBrowseId =
    selectedIteration?.designIterationId ?? selectedIteration?.id ?? null;
  const featuredIterationIdSet = new Set(
    featuredIterations
      .map((item) => extractFeaturedIterationId(item))
      .filter((id) => id != null)
      .map((id) => Number(id))
  );
  const isBrowseFeaturedForSelectedIteration =
    selectedIterBrowseId != null &&
    featuredIterationIdSet.has(Number(selectedIterBrowseId));
  const featuredMediaBySection = featuredIteration?.media ? normalizeMediaBySection(featuredIteration.media) : {};
  const featuredDesignItems = isFeaturedRequest
    ? Object.entries(featuredMediaBySection).flatMap(([section, items]) =>
      (Array.isArray(items) ? items : []).map((item, idx) =>
        (typeof item === "string" ? { key: item, section, idx } : { ...item, section, idx })
      )
    )
    : [];

  const portfolioItems = lead?.interiorDesignerPortfolioItems || [];
  const portfolioImages = portfolioItems.flatMap((item) => {
    const imgs = item.images ? (Array.isArray(item.images) ? item.images : Object.values(item.images || {})) : [];
    return imgs.map((img, idx) => ({ img, title: item.title, idx }));
  });

  const designReferenceImages = isFeaturedRequest
    ? featuredDesignItems.map((item, idx) => ({
      img: item,
      title: item?.section || "Design",
      idx,
    }))
    : portfolioImages;

  const portfolioTitle = isFeaturedRequest
    ? featuredIteration?.interactionName
      || featuredIteration?.interaction_name
      || featuredIteration?.iterationName
      || featuredIteration?.iteration_name
      || featuredIteration?.title
      || featuredIteration?.designSubmission?.title
      || featuredIteration?.design_submission?.title
      || (featuredIteration?.iterationNumber ? `Design Iteration #${featuredIteration.iterationNumber}` : "Featured Design")
    : (lead?.interiorDesignerPortfolioItems?.[0]?.title || "Interior Design Lead");
  const designReferenceLabel = isFeaturedRequest ? "Design & related items" : "Portfolio";
  const rawDesignFeesOffered =
    lead?.designFeesOffered ??
    lead?.design_fees_offered ??
    lead?.designFeeOffered ??
    lead?.design_fee_offered ??
    null;
  const numericDesignFeesOffered =
    rawDesignFeesOffered === null || rawDesignFeesOffered === undefined || rawDesignFeesOffered === ""
      ? null
      : Number(rawDesignFeesOffered);
  const formattedDesignFeesOffered =
    numericDesignFeesOffered !== null && !Number.isNaN(numericDesignFeesOffered)
      ? `Rs ${new Intl.NumberFormat("en-IN", {
        maximumFractionDigits: 2,
      }).format(numericDesignFeesOffered)}/sq.ft`
      : null;

  // Hero status-pill stats (BV parity)
  const iterationCount = requests.length;
  const finalIterationCount = requests.filter(
    (r) => Boolean(r?.iteration?.isFinal ?? r?.iteration?.is_final)
  ).length;
  const lastIterationDate = requests[0]?.createdDate || designSubmission?.createdDate || null;
  const heroTitle =
    (inboxView === "interactions" && selectedIterationName
      ? selectedIterationName
      : portfolioTitle) || "Design Request";

  return (
    <>
      <div className="flex flex-col min-h-screen w-full antialiased bg-slate-50/60">
        {/* Breadcrumb — pinned top bar with a clickable back affordance.
            Reads as a compact navigation row, not a separate page chrome. */}
        <nav
          className="w-full px-6 py-3 border-b border-slate-200/70 bg-white"
          aria-label="Breadcrumb"
        >
          <div className="flex items-center gap-2 text-sm">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center gap-1.5 text-slate-500 hover:text-flamingo-600 transition-colors font-medium"
              aria-label="Back to Leads"
            >
              <ChevronLeft size={16} className="shrink-0" aria-hidden />
              Leads
            </button>
            <span className="text-slate-300" aria-hidden>/</span>
            <span className="text-slate-700 font-medium truncate">
              {portfolioTitle && portfolioTitle !== "Interior Design Lead"
                ? portfolioTitle
                : "Lead details"}
            </span>
          </div>
        </nav>

        {/* Slate gradient hero band — mirrors BV RequestDetails. Title is the
            open iteration's name (else the design request title); subtitle is
            project · designer · unit; pills surface acknowledgement state,
            iteration count and last-update. */}
        <div className="px-4 sm:px-6 pt-4">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-600 shadow-sm">
            <div
              className="absolute inset-0 opacity-[0.08]"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 20% 0%, rgba(255,255,255,0.6) 0, transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,255,255,0.4) 0, transparent 50%)",
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900/30 via-slate-800/20 to-transparent" />

            <div className="relative px-6 py-7 sm:px-8 sm:py-8">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-white/80 backdrop-blur-sm">
                <Sparkles size={12} />
                Interior Designer · Design Request
              </div>

              <h1 className="mt-3 text-2xl sm:text-3xl font-semibold text-white">
                {heroTitle}
              </h1>

              <p className="mt-1 text-sm text-white/80">
                {heroDesignerName && (
                  <>
                    <span className="inline-flex items-center gap-1">
                      <Palette size={12} className="opacity-80" />
                      {heroDesignerName}
                    </span>
                    {heroProjectName ? <span className="text-white/50"> · </span> : null}
                  </>
                )}
                {heroProjectName ? (
                  <span className="inline-flex items-center gap-1">
                    <Building2 size={12} className="opacity-80" />
                    {heroProjectName}
                    {unitNo !== "N/A" ? ` · Unit ${unitNo}` : ""}
                  </span>
                ) : unitNo !== "N/A" ? (
                  <span className="inline-flex items-center gap-1">
                    <Home size={12} className="opacity-80" />
                    Unit {unitNo}
                  </span>
                ) : null}
              </p>

              {heroLocation && heroLocation !== "N/A" && (
                <div className="mt-2 flex items-start gap-1.5 text-xs text-white/70">
                  <MapPin size={13} className="mt-0.5 shrink-0" />
                  <span className="line-clamp-2">{heroLocation}</span>
                </div>
              )}

              {/* Status pills row */}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {lead?.isIdAcknowledged ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
                    <CheckCircle2 size={12} />
                    Acknowledged
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
                    <Clock size={12} />
                    Pending acknowledgement
                  </span>
                )}
                <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-flamingo-500 to-flamingo-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">
                  <Layers size={12} />
                  {iterationCount} {iterationCount === 1 ? "iteration" : "iterations"}
                </span>
                {lastIterationDate && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/85 backdrop-blur-sm ring-1 ring-white/10">
                    <Clock size={12} />
                    Last update {formatDate(lastIterationDate)}
                  </span>
                )}
                {finalIterationCount > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/85 backdrop-blur-sm ring-1 ring-white/10">
                    <CheckCircle2 size={12} />
                    {finalIterationCount} marked final
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {lead && (() => {
          const ackExpired = hasStatus(lead, ID_ACK_EXPIRED);
          const deliveryExpired = hasStatus(lead, ID_DELIVERY_EXPIRED);
          const iterAckExpired = hasStatus(lead, ID_ITER_ACK_EXPIRED);
          // Hide "deliver by" once a design has been submitted at least once
          // or the buyer/developer has decided on the work — at that point the
          // delivery clock no longer applies to the designer.
          const designAlreadySubmitted =
            hasStatus(lead, 'ID_DESIGN_SUBMITTED') ||
            hasStatus(lead, 'BUYER_DESIGN_APPROVED') ||
            hasStatus(lead, 'BUYER_DESIGN_REJECTED') ||
            hasStatus(lead, 'DEVELOPER_APPROVED') ||
            hasStatus(lead, 'DEVELOPER_REJECTED');
          const showAckCountdown =
            Boolean(lead.acknowledgementDeadline) && !lead.isIdAcknowledged && !ackExpired;
          const showDeliveryCountdown =
            Boolean(lead.designDeliveryDeadline) && !deliveryExpired && !designAlreadySubmitted;
          const anything =
            showAckCountdown ||
            showDeliveryCountdown ||
            ackExpired ||
            deliveryExpired ||
            iterAckExpired;
          if (!anything) return null;
          return (
            <div className="w-full px-6 py-2.5 border-b border-slate-200/70 bg-gradient-to-r from-amber-50/40 via-white to-white">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                {showAckCountdown && (
                  <CountdownBadge
                    deadline={lead.acknowledgementDeadline}
                    label="Accept by"
                    expiredLabel="Acceptance expired"
                  />
                )}
                {showDeliveryCountdown && (
                  <CountdownBadge
                    deadline={lead.designDeliveryDeadline}
                    label="Deliver by"
                    expiredLabel="Delivery overdue"
                  />
                )}
                {ackExpired && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                    <AlertTriangle size={12} className="shrink-0" aria-hidden />
                    Acceptance expired
                  </span>
                )}
                {deliveryExpired && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                    <AlertTriangle size={12} className="shrink-0" aria-hidden />
                    Delivery overdue
                  </span>
                )}
                {iterAckExpired && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                    <AlertTriangle size={12} className="shrink-0" aria-hidden />
                    Iteration feedback ack expired
                  </span>
                )}
                {(ackExpired || deliveryExpired || iterAckExpired) && (
                  <span className="text-xs text-slate-500">
                    {ackExpired
                      ? "Contact the buyer to reopen."
                      : deliveryExpired
                      ? "Delivery window closed — new iterations may not be accepted."
                      : "An iteration's feedback acknowledgement expired."}
                  </span>
                )}
              </div>
            </div>
          );
        })()}
        <div className="flex-1 flex flex-col min-h-0 w-full overflow-hidden">
          <div className="flex flex-1 min-h-0 min-w-0 w-full">
            <aside className="w-80 flex-shrink-0 border-r border-slate-200 bg-white flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto min-h-0 p-4">
                <div className="space-y-6">
                  <div>
                    {loadingDesigns ? (
                      <div className="space-y-2">
                        {[1, 2].map((i) => (
                          <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />
                        ))}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setInboxView("design_submission")}
                        className={`w-full text-left rounded-xl transition-all flex gap-3 p-3 border ${
                          inboxView === "design_submission"
                            ? "bg-flamingo-50/60 border-flamingo-200 text-slate-900 shadow-sm"
                            : "border-transparent hover:bg-slate-50 hover:border-slate-200"
                        }`}
                      >
                        <div className="min-w-0 flex-1 text-left">
                          <h4
                            className={`text-sm font-semibold ${
                              inboxView === "design_submission" ? "text-slate-900" : "text-slate-700"
                            }`}
                          >
                            Design submission
                          </h4>
                          <p
                            className={`text-xs mt-0.5 ${
                              inboxView === "design_submission" ? "text-flamingo-700" : "text-slate-500"
                            }`}
                          >
                            {designSubmission?.createdDate
                              ? formatDate(designSubmission.createdDate)
                              : lead?.isIdAcknowledged
                                ? "Not submitted yet"
                                : "Pending lead approval"}
                          </p>
                        </div>
                      </button>
                    )}
                  </div>

                  <div>
                    <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.08em] mb-2 px-1 flex items-center gap-2">
                      <span>Interactions</span>
                      {requests.filter((r) => isUnreadRequest(r)).length > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 bg-flamingo-500 text-white text-[10px] font-bold rounded-full">
                          {requests.filter((r) => isUnreadRequest(r)).length}
                        </span>
                      )}
                    </h4>
                    {loadingDesigns ? (
                      <div className="space-y-2">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />
                        ))}
                      </div>
                    ) : requests.length > 0 ? (
                      (() => {
                        const relativeOrder = ["Recent", "Yesterday", "Last week", "2 weeks ago"];
                        const grouped = requests.reduce((acc, r) => {
                          const key = formatRelativeDate(r.createdDate) || "Older";
                          if (!acc[key]) acc[key] = [];
                          acc[key].push(r);
                          return acc;
                        }, {});
                        const otherKeys = Object.keys(grouped).filter((k) => !relativeOrder.includes(k));
                        const sortedOther = otherKeys.sort((a, b) => {
                          const dA = grouped[a][0]?.createdDate ? new Date(grouped[a][0].createdDate).getTime() : 0;
                          const dB = grouped[b][0]?.createdDate ? new Date(grouped[b][0].createdDate).getTime() : 0;
                          return dB - dA;
                        });
                        const sectionKeys = [...relativeOrder.filter((k) => grouped[k]?.length), ...sortedOther];
                        return (
                          <div className="space-y-4">
                            {sectionKeys.map((sectionKey) => (
                              <div key={sectionKey}>
                                <h5 className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.08em] mb-1.5 px-1 sticky top-0 bg-white py-1">
                                  {sectionKey}
                                </h5>
                                <div className="space-y-1">
                                  {grouped[sectionKey].map((request) => {
                                    const isSelected =
                                      inboxView === "interactions" && selectedIterationId === request.id;
                                    const isUnread = isUnreadRequest(request);
                                    const isFinalIteration = Boolean(
                                      request?.iteration?.isFinal ?? request?.iteration?.is_final
                                    );
                                    const rowIterId =
                                      request?.iteration?.designIterationId ??
                                      request?.iteration?.id ??
                                      request?.id ??
                                      null;
                                    const isRowFeatured =
                                      rowIterId != null && featuredIterationIdSet.has(Number(rowIterId));
                                    // Inbox row title: prefer the iteration's own name; fall back to
                                    // the parent design submission name. Buyer feedback / comments
                                    // are intentionally not used here.
                                    const iterationName =
                                      request.iteration?.iterationName?.trim?.() ||
                                     
                                      "";
                                    const displayTitle =
                                      iterationName ||
                                      request.title ||
                                      "Design Submission";
                                    return (
                                      <div
                                        key={request.id}
                                        onClick={() => {
                                          setInboxView("interactions");
                                          setSelectedIterationId(request.id);
                                          setSelectedIterationData(request.iteration);
                                          setSelectedSubmissionData(request.submission);
                                          setAcknowledgeRejectNote("");
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            setInboxView("interactions");
                                            setSelectedIterationId(request.id);
                                            setSelectedIterationData(request.iteration);
                                            setSelectedSubmissionData(request.submission);
                                            setAcknowledgeRejectNote("");
                                          }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        className={`relative w-full text-left rounded-xl transition-all flex gap-3 p-3 pl-4 border ${
                                          isSelected
                                            ? "bg-flamingo-50/60 border-flamingo-200 shadow-sm"
                                            : isUnread
                                              ? "bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                                              : "border-transparent hover:bg-slate-50 hover:border-slate-200"
                                        }`}
                                      >
                                        {/* Left accent bar — flamingo gradient on
                                            the selected item, subtle slate on
                                            others. Gives the list a clear "where
                                            am I?" focal anchor. */}
                                        {isSelected && (
                                          <span
                                            className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-gradient-to-b from-flamingo-400 to-flamingo-600"
                                            aria-hidden
                                          />
                                        )}
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center gap-2 min-w-0">
                                            <h4 className={`text-sm font-semibold truncate line-clamp-2 ${
                                              isSelected ? "text-slate-900" : isUnread ? "text-slate-900" : "text-slate-600"
                                            }`}>
                                              {displayTitle}
                                            </h4>
                                            {isFinalIteration && (
                                              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0">
                                                Final
                                              </span>
                                            )}
                                            {isRowFeatured && (
                                              <span
                                                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200 shrink-0"
                                                title="Featured on buyer browse"
                                              >
                                                <Star size={10} className="shrink-0 fill-amber-500 text-amber-600" aria-hidden />
                                                Featured
                                              </span>
                                            )}
                                          </div>
                                          <p className={`text-xs mt-0.5 ${isSelected ? "text-flamingo-700" : "text-slate-500"}`}>
                                            {formatDate(request.createdDate)}
                                          </p>
                                          {(() => {
                                            const sd = getIterationStatusDisplay(request?.iteration);
                                            return (
                                              <span
                                                className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border ${STATUS_TONE_CLASSES[sd.tone]}`}
                                              >
                                                {sd.label}
                                              </span>
                                            );
                                          })()}
                                          {(() => {
                                            const iter = request?.iteration;
                                            const iterAckDeadline = iter?.idAcknowledgementDeadline;
                                            const iterStatus = (iter?.iterationStatus ?? iter?.iteration_status ?? '').toString().toUpperCase();
                                            const iterRejected = iterStatus === "REJECTED";
                                            const iterDecided = iterStatus === 'APPROVED' || iterRejected;
                                            const showIterAck =
                                              Boolean(iterAckDeadline) && iter && !iter.isIdAcknowledged && !iterDecided;
                                            const showIterExpired =
                                              iterRejected && hasStatus(lead, ID_ITER_ACK_EXPIRED);
                                            if (!showIterAck && !showIterExpired) return null;
                                            return (
                                              <div className="mt-1 flex flex-wrap items-center gap-1">
                                                {showIterAck && (
                                                  <CountdownBadge
                                                    deadline={iterAckDeadline}
                                                    label="Ack by"
                                                    expiredLabel="Ack expired"
                                                  />
                                                )}
                                                {showIterExpired && !showIterAck && (
                                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700 border border-rose-300">
                                                    <AlertTriangle size={12} className="shrink-0" aria-hidden />
                                                    Feedback ack expired
                                                  </span>
                                                )}
                                              </div>
                                            );
                                          })()}
                                        </div>
                                        {isUnread && !isSelected && (
                                          <span className="relative flex h-2 w-2 mt-1.5 shrink-0" aria-label="Unread">
                                            <span className="absolute inline-flex h-full w-full rounded-full bg-flamingo-400 opacity-75 animate-ping" />
                                            <span className="relative inline-flex h-2 w-2 rounded-full bg-flamingo-500" />
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()
                    ) : (
                      <div className="text-center py-10 px-2 rounded-xl border border-dashed border-slate-200 bg-slate-50/40">
                        <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white border border-slate-200 mb-3 shadow-sm">
                          <FileText className="text-slate-400" size={18} />
                        </div>
                        <p className="text-sm text-slate-700 font-semibold">No iterations yet</p>
                        <p className="text-xs text-slate-500 mt-1 max-w-[16rem] mx-auto leading-relaxed">
                          They appear after you submit a design and the buyer responds.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </aside>
            <div className="flex-1 min-w-0 overflow-y-auto bg-gray-50/30">
              {inboxView === "design_submission" ? (
              <div className="w-full ">
                {/* Simplified design conversation — one thread + the current phase at a glance. */}
                {(conversation?.deliverables?.length > 0 || loadingConversation) && (
                  <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <h3 className="text-base font-semibold text-slate-900">Design conversation</h3>
                      {conversation?.status && <DesignPhaseBadge phase={conversation.status} />}
                    </div>
                    {loadingConversation && !conversation ? (
                      <p className="text-sm text-slate-400">Loading conversation…</p>
                    ) : (
                      <DesignConversation deliverables={conversation?.deliverables || []} perspective="DESIGNER" />
                    )}
                  </div>
                )}
                {/* Inline Design Request review – show specs and Accept/Decline until accepted */}
                {!lead.isIdAcknowledged && lead && (() => {
                  const ackExpired = hasStatus(lead, ID_ACK_EXPIRED);
                  return (
                    <AmberReviewRequestPanel
                      className="mb-4 p-2"
                      title="Review design request"
                      actions={
                        <>
                          <DeleteButton
                            onClick={handleReject}
                            disabled={approving || ackExpired}
                            className="border-amber-300 text-amber-800 hover:bg-amber-50"
                          >
                            Decline
                          </DeleteButton>
                          <PrimaryButton
                            onClick={handleApprove}
                            disabled={approving || ackExpired}
                            title={ackExpired ? "This request expired — contact the buyer to reopen." : undefined}
                          >
                            {approving ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Processing...
                              </>
                            ) : (
                              "Accept request"
                            )}
                          </PrimaryButton>
                        </>
                      }
                    >
                      {ackExpired && (
                        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 flex items-start gap-2">
                          <AlertTriangle size={14} className="shrink-0 mt-0.5" aria-hidden />
                          <span>
                            This request expired before it was acknowledged. Contact the buyer to reopen it before
                            you can accept or decline.
                          </span>
                        </div>
                      )}
                      {Boolean(lead.acknowledgementDeadline) && !ackExpired && (
                        <CountdownBadge
                          deadline={lead.acknowledgementDeadline}
                          label="Acknowledge by"
                          expiredLabel="Acknowledgement expired"
                          size="md"
                          compact={false}
                        />
                      )}
                      {(isFeaturedRequest || lead.interiorDesignerPortfolioItems?.[0]) && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
                            {isFeaturedRequest ? "Selected design -" : "Selected portfolio -"}
                          </h3>
                          <p className="text-sm text-gray-900 font-medium">{portfolioTitle}</p>
                        </div>
                      )}
                      {formattedDesignFeesOffered && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
                            Design fees offered (Rs/sq.ft)
                          </h3>
                          <p className="text-sm text-gray-900 font-medium">{formattedDesignFeesOffered}</p>
                        </div>
                      )}
                    </AmberReviewRequestPanel>
                  );
                })()}

                {/* Lead summary — dark-slate gradient hero with flamingo
                    accents. Mirrors the SoldPropertyDetails hero treatment
                    elsewhere in the app, so the page reads as a proper
                    destination. Icon medallion + title + status chip on
                    the hero band; property stats render as inline pills
                    on the same dark background for impact. */}
                <div className="mx-3 lg:mx-4 mt-3 lg:mt-4 mb-3 rounded-2xl shadow-sm overflow-hidden">
                  <div className="relative px-5 lg:px-6 py-5 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700">
                    {/* Subtle radial highlight to give the band depth */}
                    <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_20%_20%,white,transparent_55%)] pointer-events-none" />
                    <div className="relative flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-11 h-11 rounded-xl bg-flamingo-500/20 border border-flamingo-500/30 flex items-center justify-center shrink-0">
                          <Sparkles size={20} className="text-flamingo-300" aria-hidden />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[10px] text-flamingo-300/90 uppercase tracking-[0.12em] font-semibold mb-1">
                            Interior Design Lead
                          </p>
                          <h1 className="text-lg sm:text-xl font-semibold text-white tracking-tight truncate">
                            {portfolioTitle}
                          </h1>
                          {/* Inline meta strip — date + buyer-facing context */}
                          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-300">
                            <span className="inline-flex items-center gap-1">
                              <Clock size={12} className="text-slate-400" aria-hidden />
                              {formatDate(lead.createdDate)}
                            </span>
                            {formattedDesignFeesOffered && (
                              <span className="inline-flex items-center gap-1">
                                <span className="text-slate-500">·</span>
                                <span className="text-flamingo-200 font-medium tabular-nums">
                                  ₹{formattedDesignFeesOffered}
                                </span>
                                <span className="text-slate-500">/sq.ft</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="shrink-0">
                        {lead.isIdAcknowledged ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/15 text-emerald-300 text-xs font-semibold rounded-full border border-emerald-400/30">
                            <span className="relative flex h-2 w-2">
                              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                            </span>
                            Approved
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/15 text-amber-200 text-xs font-semibold rounded-full border border-amber-400/30">
                            <Clock size={12} className="shrink-0" aria-hidden />
                            Pending review
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Property stats — inline pills on the dark band */}
                    <div className="relative mt-4 flex flex-wrap gap-2">
                      {unitNo !== "N/A" && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-200">
                          <Home size={12} className="text-flamingo-300/80 shrink-0" aria-hidden />
                          <span className="text-slate-400">Unit</span>
                          <span className="font-semibold text-white">{unitNo}</span>
                        </span>
                      )}
                      {configName !== "N/A" && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-200">
                          <Layers size={12} className="text-flamingo-300/80 shrink-0" aria-hidden />
                          <span className="text-slate-400">Config</span>
                          <span className="font-semibold text-white">{configName}</span>
                        </span>
                      )}
                      {configType !== "N/A" && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-200">
                          <span className="text-slate-400">Type</span>
                          <span className="font-semibold text-white">{configType}</span>
                        </span>
                      )}
                      {area !== "N/A" && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-200">
                          <Ruler size={12} className="text-flamingo-300/80 shrink-0" aria-hidden />
                          <span className="text-slate-400">Area</span>
                          <span className="font-semibold text-white">{area}</span>
                        </span>
                      )}
                      {towerAndFloor !== "N/A" && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-200">
                          <span className="text-slate-400">Tower / floor</span>
                          <span className="font-semibold text-white">{towerAndFloor}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Light card body — keeps the existing inner sections (design fees, requirements) inside the same outer card */}
                  <div className="bg-white border-x border-b border-slate-200 rounded-b-2xl">

                  {/* Design fees + Submit design — side-by-side on the same row */}
                  {(formattedDesignFeesOffered || (lead.isIdAcknowledged && !designSubmission)) && (
                    <div className="px-5 py-4 border-t border-slate-100 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                      {formattedDesignFeesOffered ? (
                        <div className="min-w-0">
                          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Design fees offered (Rs/sq.ft)</p>
                          <p className="text-base font-semibold text-slate-900 mt-0.5 tabular-nums">{formattedDesignFeesOffered}</p>
                        </div>
                      ) : (
                        <div className="hidden sm:block" />
                      )}

                      {lead.isIdAcknowledged && !designSubmission ? (
                        <button
                          onClick={() => { setIterationUploadSubmission(null); setDesignModalStep(0); setShowDesignModal(true); }}
                          className="w-full sm:w-auto sm:shrink-0 px-4 py-2 bg-flamingo-600 text-white text-sm font-semibold rounded-lg hover:bg-flamingo-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
                        >
                          <Upload size={14} />
                          Submit design
                        </button>
                      ) : null}
                    </div>
                  )}

                  {lead.requirements && (
                    <div className="px-5 py-4 border-t border-slate-100">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Requirements</p>
                        <button
                          type="button"
                          onClick={() => setShowRequirementsModal(true)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-flamingo-50 border border-gray-200 hover:border-flamingo-200 rounded-md transition-colors text-sm font-medium"
                          title="Expand in modal"
                          aria-label="Expand requirements"
                        >
                          <Maximize2 size={18} />
                          <span>Expand</span>
                        </button>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                        <div className="requirements-content overflow-y-auto p-4 text-sm max-h-80 min-h-[80px]">
                          <style>{`
                            .requirements-content { font-family: "Gotham", sans-serif; font-weight: 300; }
                            .requirements-content h1, .requirements-content h2 { font-size: 1.125rem; font-weight: 600; color: #111827; margin: 1rem 0 0.5rem; }
                            .requirements-content h3 { font-size: 1rem; font-weight: 600; color: #111827; margin: 0.75rem 0 0.5rem; }
                            .requirements-content p { margin: 0.5rem 0; color: #374151; line-height: 1.6; font-size: 0.875rem; }
                            .requirements-content ul { margin: 0.5rem 0; padding-left: 1.5rem; color: #374151; }
                            .requirements-content li { margin: 0.25rem 0; line-height: 1.5; font-size: 0.875rem; }
                            .requirements-content strong { font-weight: 600; color: #111827; }
                            .requirements-content u { text-decoration: underline; }
                            .requirements-content blockquote { border-left: 4px solid #e5e7eb; padding-left: 1rem; margin: 0.5rem 0; color: #6b7280; font-style: italic; }
                          `}</style>
                          <div dangerouslySetInnerHTML={{ __html: lead.requirements }} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Buyer reference images (lead.media) — shown via presigned
                      S3 URLs. Legacy base64 leads (media as a raw string) are
                      intentionally not rendered. */}
                  {Array.isArray(lead?.media) && lead.media.length > 0 && (
                    <div className="px-5 py-4 border-t border-slate-100">
                      <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em] mb-2">
                        Buyer reference images
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {lead.media.map((m, i) => {
                          const key = typeof m === "string" ? null : (m?.s3_key ?? m?.s3Key ?? m?.key);
                          const src = key ? leadMediaUrls[key] : getImageSrc(m);
                          if (!src) return null;
                          return (
                            <a
                              key={key || i}
                              href={src}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block w-20 h-20 rounded-lg overflow-hidden border border-slate-200 hover:border-flamingo-300"
                            >
                              <LoadingImage
                                src={src}
                                alt={`Reference ${i + 1}`}
                                containerClassName="w-full h-full"
                                className="w-full h-full object-cover"
                              />
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Status timeline — collapsible history of the lead's lifecycle */}
                  <details className="px-5 py-4 border-t border-slate-100 group" open>
                    <summary className="flex items-center justify-between cursor-pointer list-none">
                      <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Timeline</p>
                      <ChevronLeft
                        size={16}
                        className="text-slate-400 transition-transform group-open:-rotate-90"
                      />
                    </summary>
                    <div className="mt-4">
                      <LeadStatusTimeline lead={lead} />
                    </div>
                  </details>
                  </div>{/* /white body wrapper */}
                </div>{/* /outer lead summary card */}

                <div className="p-3 lg:p-4">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 space-y-4">
                {/* Config images and Floor plans – from /property/config/media */}
                {configImages.length > 0 && (() => {
                  // Separate floor plans from regular images
                  const floorPlans = configImages.filter(media => {
                    const nameLower = (media.name || "").toLowerCase();
                    const keyLower = (media.key || "").toLowerCase();
                    return nameLower.includes("floor") ||
                      nameLower.includes("plan") ||
                      keyLower.includes("floor") ||
                      keyLower.includes("plan") ||
                      media.isPdf;
                  });
                  const regularImages = configImages.filter(media => {
                    const nameLower = (media.name || "").toLowerCase();
                    const keyLower = (media.key || "").toLowerCase();
                    return !(nameLower.includes("floor") ||
                      nameLower.includes("plan") ||
                      keyLower.includes("floor") ||
                      keyLower.includes("plan") ||
                      media.isPdf);
                  });

                  return (
                    <>
                      {/* Floor Plans Section */}
                      {floorPlans.length > 0 && (
                        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm mb-4">
                          <h3 className="text-md font-medium text-gray-900 mb-2 flex items-center gap-1.5">
                           
                            Floor Plans & Documents
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {floorPlans.map((media, index) => (
                              <div
                                key={index}
                                className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden hover:border-gray-300 transition-colors relative group"
                              >
                                {media.isImage && media.url ? (
                                  <>
                                    <div className="aspect-video bg-gray-100 relative">
                                      <LoadingImage
                                        src={media.url}
                                        alt={media.name || `Floor Plan ${index + 1}`}
                                        containerClassName="w-full h-full"
                                        className="w-full h-full object-contain p-2"
                                        fallback={
                                          <div className="absolute inset-0 flex flex-col items-center justify-center p-3 bg-gray-50">
                                            <FileText className="text-gray-400" size={24} />
                                            <p className="text-sm text-gray-600 text-center truncate w-full mt-2">{media.name}</p>
                                          </div>
                                        }
                                      />
                                    </div>
                                    <div className="p-2 border-t border-gray-200 bg-white">
                                      <p className="text-sm text-gray-700 font-medium truncate">{media.name || `Floor Plan ${index + 1}`}</p>
                                    </div>
                                  </>
                                ) : (
                                  <div className="p-4 flex flex-col items-center justify-center min-h-[120px]">
                                    <FileText className="text-gray-400 mb-2" size={32} />
                                    <p className="text-sm text-gray-600 text-center font-medium">{media.name || `Document ${index + 1}`}</p>
                                    {media.isPdf && (
                                      <span className="text-[10px] text-gray-500 mt-1">PDF Document</span>
                                    )}
                                  </div>
                                )}
                                {media.url && (
                                  <a
                                    href={media.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`Open ${media.name || `document ${index + 1}`} in a new tab`}
                                    className="absolute inset-0 z-10"
                                    onClick={(e) => {
                                      if (!media.isImage) {
                                        e.preventDefault();
                                        window.open(media.url, '_blank');
                                      }
                                    }}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Config Images + Portfolio – side by side with carousels */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                        {/* Config images panel with carousel */}
                        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                          <h3 className="text-md font-medium text-gray-900 mb-2 flex items-center gap-1.5">
                           
                            Config Images
                          </h3>
                          {regularImages.length > 0 ? (
                            <div className="relative flex justify-center items-center p-4">
                              {regularImages.length > 1 && (
                                <div className="absolute -translate-x-20 scale-90 opacity-20 z-0 pointer-events-none">
                                  <LoadingImage
                                    src={regularImages[(configCarouselIndex - 1 + regularImages.length) % regularImages.length]?.url}
                                    alt="Previous"
                                    containerClassName="w-40 h-40 rounded-lg"
                                    className="w-40 h-40 object-cover rounded-lg"
                                  />
                                </div>
                              )}
                              <div className="relative z-10">
                                {(() => {
                                  const media = regularImages[configCarouselIndex];
                                  const url = media?.url;
                                  return url ? (
                                    <a href={url} target="_blank" rel="noopener noreferrer" className="block relative">
                                      <LoadingImage
                                        src={url}
                                        alt={media?.name || `Config ${configCarouselIndex + 1}`}
                                        containerClassName="w-full max-w-[240px] aspect-square rounded-lg shadow-lg mx-auto"
                                        className="w-full max-w-[240px] aspect-square object-cover rounded-lg shadow-lg mx-auto"
                                        fallback={
                                          <div className="absolute inset-0 flex flex-col items-center justify-center p-3 bg-gray-50">
                                            <FileText className="text-gray-400" size={24} />
                                            <p className="text-sm text-gray-600 truncate">{media?.name}</p>
                                          </div>
                                        }
                                      />
                                    </a>
                                  ) : (
                                    <div className="w-40 h-40 flex flex-col items-center justify-center rounded-lg bg-gray-50 border border-gray-200">
                                      <FileText className="text-gray-400" size={24} />
                                      <p className="text-sm text-gray-600 truncate">{media?.name}</p>
                                    </div>
                                  );
                                })()}
                              </div>
                              {regularImages.length > 1 && (
                                <>
                                  <div className="absolute translate-x-20 scale-90 opacity-20 z-0 pointer-events-none">
                                    <LoadingImage
                                      src={regularImages[(configCarouselIndex + 1) % regularImages.length]?.url}
                                      alt="Next"
                                      containerClassName="w-40 h-40 rounded-lg"
                                      className="w-40 h-40 object-cover rounded-lg"
                                    />
                                  </div>
                                  <div className="absolute top-1/2 -translate-y-1/2 flex justify-between w-full px-1 z-20">
                                    <button
                                      onClick={() => setConfigCarouselIndex((i) => (i > 0 ? i - 1 : regularImages.length - 1))}
                                      className="bg-white/90 hover:bg-white rounded-full p-1.5 shadow-lg text-gray-600 hover:text-gray-900"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => setConfigCarouselIndex((i) => (i < regularImages.length - 1 ? i + 1 : 0))}
                                      className="bg-white/90 hover:bg-white rounded-full p-1.5 shadow-lg text-gray-600 hover:text-gray-900"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                      </svg>
                                    </button>
                                  </div>
                                  <p className="text-center text-[10px] text-gray-500 mt-2">{configCarouselIndex + 1} / {regularImages.length}</p>
                                </>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center justify-center py-10 text-gray-400 border border-dashed border-gray-200 rounded-lg">
                              <ImageIcon size={18} className="mr-2" />
                              <span className="text-sm">No config images</span>
                            </div>
                          )}
                        </div>

                        {/* Design reference panel with carousel */}
                        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                          <h3 className="text-md font-normal text-gray-900 mb-2 flex items-center gap-1.5">
                           
                            {designReferenceLabel}
                          </h3>
                          {(() => {
                            if (designReferenceImages.length === 0) {
                              return (
                                <div className="flex items-center justify-center py-10 text-gray-400 border border-dashed border-gray-200 rounded-lg">
                                  <ImageIcon size={18} className="mr-2" />
                                  <span className="text-sm">{isFeaturedRequest ? "No design items" : "No portfolio images"}</span>
                                </div>
                              );
                            }
                            const current = designReferenceImages[portfolioCarouselIndex];
                            const src = getImageSrc(current?.img);
                            return (
                              <div className="relative flex justify-center items-center p-4">
                                {designReferenceImages.length > 1 && (
                                  <div className="absolute -translate-x-20 scale-90 opacity-20 z-0 pointer-events-none">
                                    <LoadingImage
                                      src={getImageSrc(designReferenceImages[(portfolioCarouselIndex - 1 + designReferenceImages.length) % designReferenceImages.length]?.img)}
                                      alt="Previous"
                                      containerClassName="w-40 h-40 rounded-lg"
                                      className="w-40 h-40 object-cover rounded-lg"
                                    />
                                  </div>
                                )}
                                <div className="relative z-10">
                                  {src ? (
                                    <a href={src} target="_blank" rel="noopener noreferrer" className="block">
                                      <LoadingImage
                                        src={src}
                                        alt={`${current?.title || (isFeaturedRequest ? "Design" : "Portfolio")} ${(current?.idx || 0) + 1}`}
                                        containerClassName="w-full max-w-[240px] aspect-square rounded-lg shadow-lg mx-auto"
                                        className="w-full max-w-[240px] aspect-square object-cover rounded-lg shadow-lg mx-auto"
                                      />
                                    </a>
                                  ) : (
                                    <div className="w-40 h-40 flex items-center justify-center rounded-lg bg-gray-50 border border-gray-200">
                                      <ImageIcon className="text-gray-400" size={24} />
                                    </div>
                                  )}
                                </div>
                                {designReferenceImages.length > 1 && (
                                  <>
                                    <div className="absolute translate-x-20 scale-90 opacity-20 z-0 pointer-events-none">
                                      <LoadingImage
                                        src={getImageSrc(designReferenceImages[(portfolioCarouselIndex + 1) % designReferenceImages.length]?.img)}
                                        alt="Next"
                                        containerClassName="w-40 h-40 rounded-lg"
                                        className="w-40 h-40 object-cover rounded-lg"
                                      />
                                    </div>
                                    <div className="absolute top-1/2 -translate-y-1/2 flex justify-between w-full px-1 z-20">
                                      <button
                                        onClick={() => setPortfolioCarouselIndex((i) => (i > 0 ? i - 1 : designReferenceImages.length - 1))}
                                        className="bg-white/90 hover:bg-white rounded-full p-1.5 shadow-lg text-gray-600 hover:text-gray-900"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                                        </svg>
                                      </button>
                                      <button
                                        onClick={() => setPortfolioCarouselIndex((i) => (i < designReferenceImages.length - 1 ? i + 1 : 0))}
                                        className="bg-white/90 hover:bg-white rounded-full p-1.5 shadow-lg text-gray-600 hover:text-gray-900"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                        </svg>
                                      </button>
                                    </div>
                                    <p className="text-center text-[10px] text-gray-500 mt-2">{portfolioCarouselIndex + 1} / {designReferenceImages.length}</p>
                                  </>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </>
                  );
                })()}

                {/* Design reference only (when no config images – show standalone carousel) */}
                {designReferenceImages.length > 0 && configImages.length === 0 && (
                  <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm mb-4">
                    <h3 className="text-md font-medium text-gray-900 mb-2 flex items-center gap-1.5">
                      
                      {designReferenceLabel}
                    </h3>
                    <div className="relative flex justify-center items-center p-4">
                      {(() => {
                        if (designReferenceImages.length === 0) {
                          return (
                            <div className="flex items-center justify-center py-10 text-gray-400 border border-dashed border-gray-200 rounded-lg">
                              <ImageIcon size={18} className="mr-2" />
                              <span className="text-sm">{isFeaturedRequest ? "No design items" : "No portfolio images"}</span>
                            </div>
                          );
                        }
                        const current = designReferenceImages[portfolioCarouselIndex];
                        const src = getImageSrc(current?.img);
                        return (
                          <>
                            {designReferenceImages.length > 1 && (
                              <div className="absolute -translate-x-20 scale-90 opacity-20 z-0 pointer-events-none">
                                <LoadingImage src={getImageSrc(designReferenceImages[(portfolioCarouselIndex - 1 + designReferenceImages.length) % designReferenceImages.length]?.img)} alt="Previous" containerClassName="w-40 h-40 rounded-lg" className="w-40 h-40 object-cover rounded-lg" />
                              </div>
                            )}
                            <div className="relative z-10">
                              {src ? (
                                <a href={src} target="_blank" rel="noopener noreferrer" className="block">
                                  <LoadingImage src={src} alt={`${current?.title || (isFeaturedRequest ? "Design" : "Portfolio")} ${(current?.idx || 0) + 1}`} containerClassName="w-full max-w-[280px] aspect-square rounded-lg shadow-lg mx-auto" className="w-full max-w-[280px] aspect-square object-cover rounded-lg shadow-lg mx-auto" />
                                </a>
                              ) : (
                                <div className="w-40 h-40 flex items-center justify-center rounded-lg bg-gray-50 border border-gray-200">
                                  <ImageIcon className="text-gray-400" size={24} />
                                </div>
                              )}
                            </div>
                            {designReferenceImages.length > 1 && (
                              <>
                                <div className="absolute translate-x-20 scale-90 opacity-20 z-0 pointer-events-none">
                                  <LoadingImage src={getImageSrc(designReferenceImages[(portfolioCarouselIndex + 1) % designReferenceImages.length]?.img)} alt="Next" containerClassName="w-40 h-40 rounded-lg" className="w-40 h-40 object-cover rounded-lg" />
                                </div>
                                <div className="absolute top-1/2 -translate-y-1/2 flex justify-between w-full px-1 z-20">
                                  <button onClick={() => setPortfolioCarouselIndex((i) => (i > 0 ? i - 1 : designReferenceImages.length - 1))} className="bg-white/90 hover:bg-white rounded-full p-1.5 shadow-lg text-gray-600 hover:text-gray-900">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
                                  </button>
                                  <button onClick={() => setPortfolioCarouselIndex((i) => (i < designReferenceImages.length - 1 ? i + 1 : 0))} className="bg-white/90 hover:bg-white rounded-full p-1.5 shadow-lg text-gray-600 hover:text-gray-900">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                                  </button>
                                </div>
                                <p className="text-center text-[10px] text-gray-500 mt-2">{portfolioCarouselIndex + 1} / {designReferenceImages.length}</p>
                              </>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
                    </div>

                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                      <div className="px-4 py-3 border-b border-slate-100">
                        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                          <Clock size={16} className="text-flamingo-600" />
                          Activity
                        </h3>
                      </div>
                      <div className="p-3 space-y-3">
                        {designSubmission?.createdDate ? (
                          <div className="flex gap-3">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                              <CheckCircle2 size={14} className="text-green-700" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-0.5">
                                Design submitted
                              </p>
                              <p className="text-sm text-gray-500 mt-1">{formatDate(designSubmission.createdDate)}</p>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 py-4 text-center">No submission date yet</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              ) : (
                  <>
                  {selectedIterationId && selectedIteration && selectedSubmission ? (
              /* Iteration detail */
              <div className="w-full">
                {/* Inline Iteration feedback review – show on top until designer accepts/declines.
                    Only the latest iteration is acknowledgeable; a superseded one is frozen. */}
                {selectedIteration.buyerFeedback && !selectedIteration.isIdAcknowledged && !isSelectedIterationLatest && (
                  <div className="mx-3 lg:mx-4 mt-3 mb-3 p-3 rounded-2xl border border-slate-200 bg-slate-50 text-sm text-slate-600 shadow-sm">
                    This iteration was superseded by a newer one — its feedback no longer needs acknowledgement.
                  </div>
                )}
                {selectedIteration.buyerFeedback && !selectedIteration.isIdAcknowledged && isSelectedIterationLatest && (
                  <AmberReviewRequestPanel
                    className="mx-3 lg:mx-4 mt-3 mb-3 p-2 relative z-10"
                    title="Review buyer feedback"
                    description='Accept or decline this feedback to unlock "Upload updated design" and "Edit BOQ".'
                    headerBadge={
                      <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 bg-amber-200/90 text-amber-900 text-xs font-semibold rounded border border-amber-300/60">
                        Action required
                      </span>
                    }
                    actions={
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setDeclineNote("");
                            setShowDeclineModal(true);
                          }}
                          disabled={acknowledging}
                          className="inline-flex items-center justify-center gap-1.5 h-8 px-3 text-xs font-semibold text-red-700 bg-white border border-red-200 rounded-md hover:bg-red-50 hover:border-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <X size={14} aria-hidden />
                          Decline
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowAcceptConfirmModal(true)}
                          disabled={acknowledging}
                          className="inline-flex items-center justify-center gap-1.5 h-8 px-3 text-xs font-semibold text-white bg-flamingo-600 rounded-md hover:bg-flamingo-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {acknowledging ? (
                            <>
                              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Processing…
                            </>
                          ) : (
                            <>
                              <Check size={14} aria-hidden />
                              Accept
                            </>
                          )}
                        </button>
                      </div>
                    }
                  >
                    <div className="p-3 bg-white/70 border border-amber-200/60 rounded-lg w-full min-w-[200px] max-w-2xl space-y-2">
                      {(() => {
                        const itStatus = (selectedIteration?.iterationStatus ?? selectedIteration?.iteration_status ?? '').toString().toUpperCase();
                        const decided = itStatus === 'APPROVED' || itStatus === 'REJECTED';
                        const shouldShow =
                          Boolean(selectedIteration.idAcknowledgementDeadline) &&
                          !selectedIteration.isIdAcknowledged &&
                          !decided;
                        if (!shouldShow) return null;
                        return (
                          <CountdownBadge
                            deadline={selectedIteration.idAcknowledgementDeadline}
                            label="Ack feedback by"
                            expiredLabel="Feedback ack expired"
                            size="md"
                            compact={false}
                          />
                        );
                      })()}
                      <div>
                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-1">Buyer message</p>
                        <p className="text-sm text-gray-800 leading-relaxed">{selectedIteration.buyerFeedback}</p>
                        {selectedIteration.feedbackDate && (
                          <p className="text-sm text-gray-500 mt-1.5">{formatDateTime(selectedIteration.feedbackDate)}</p>
                        )}
                      </div>
                    </div>
                  </AmberReviewRequestPanel>
                )}

                {/* Iteration detail header — sibling of the lead summary
                    hero. Light gradient (slate-50 → white) keeps it readable
                    against the action buttons but still distinguishes it from
                    the rest of the page. Flamingo accent on the price chip
                    is the visual hook. */}
                <div className="mx-3 lg:mx-4 mt-3 lg:mt-4 mb-3 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="px-4 py-4 lg:px-5 lg:py-5 bg-gradient-to-br from-slate-50 via-white to-white">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    {/* Left: Title, Price, Property Details */}
                    <div className="flex-1 min-w-0 space-y-3">
                      {/* Title row — icon medallion + heading + chips */}
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-flamingo-500 to-flamingo-600 flex items-center justify-center shrink-0 shadow-sm">
                          <ImageIcon size={18} className="text-white" aria-hidden />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] text-flamingo-600 uppercase tracking-[0.12em] font-semibold mb-0.5">
                            Design iteration
                          </p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">
                              {selectedIterationName || selectedSubmission.title || "Design Submission"}
                            </h1>
                            {(() => {
                              const sd = getIterationStatusDisplay(selectedIteration);
                              return (
                                <span
                                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold border ${STATUS_TONE_CLASSES[sd.tone]}`}
                                >
                                  {sd.label}
                                </span>
                              );
                            })()}
                            {Boolean(selectedIteration?.isFinal ?? selectedIteration?.is_final) && (
                              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <CheckCircle2 size={11} aria-hidden />
                                Final request
                              </span>
                            )}
                            {selectedIteration.estimatedPrice ? (
                              <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white border border-flamingo-600 shadow-sm tabular-nums">
                                ₹{parseFloat(selectedIteration.estimatedPrice).toLocaleString("en-IN")}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      {/* Property Details — compact key/value grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm">
                        {unitNo !== "N/A" && (
                          <div>
                            <p className="text-[11px] text-slate-500 uppercase tracking-wider font-medium">Unit</p>
                            <p className="text-slate-900 font-medium text-sm mt-0.5">{unitNo}</p>
                          </div>
                        )}
                        {configName !== "N/A" && (
                          <div>
                            <p className="text-[11px] text-slate-500 uppercase tracking-wider font-medium">Config</p>
                            <p className="text-slate-900 font-medium text-sm mt-0.5">{configName}</p>
                          </div>
                        )}
                        {configType !== "N/A" && (
                          <div>
                            <p className="text-[11px] text-slate-500 uppercase tracking-wider font-medium">Type</p>
                            <p className="text-slate-900 font-medium text-sm mt-0.5">{configType}</p>
                          </div>
                        )}
                        {area !== "N/A" && (
                          <div>
                            <p className="text-[11px] text-slate-500 uppercase tracking-wider font-medium">Area</p>
                            <p className="text-slate-900 font-medium text-sm mt-0.5">{area}</p>
                          </div>
                        )}
                        {portfolioTitle !== "Interior Design Lead" && (
                          <div className="min-w-0">
                            <p className="text-[11px] text-slate-500 uppercase tracking-wider font-medium">
                              {isFeaturedRequest ? "Design" : "Portfolio"}
                            </p>
                            <p className="text-slate-900 font-medium text-sm mt-0.5 truncate">{portfolioTitle}</p>
                          </div>
                        )}
                        {selectedSubmission.createdDate && (
                          <div>
                            <p className="text-[11px] text-slate-500 uppercase tracking-wider font-medium">Submitted</p>
                            <p className="text-slate-900 font-medium text-sm mt-0.5">{formatDate(selectedSubmission.createdDate)}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right: Featured + BOQ + Upload action cluster.
                        Secondary actions are outline-style, primary action is
                        the flamingo-filled "Upload Design". Cluster is
                        right-aligned and wraps cleanly under narrow widths. */}
                    <div className="flex-shrink-0 flex flex-wrap items-center justify-end gap-2">
                      {selectedIterBrowseId != null &&
                        (browseProjectPhaseId == null || browseProjectPhaseId === "" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200" title="Lead has no project phase">
                            <AlertTriangle size={11} className="shrink-0" aria-hidden />
                            No phase
                          </span>
                        ) : isBrowseFeaturedForSelectedIteration ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                            <Star size={11} className="shrink-0 fill-amber-500 text-amber-600" aria-hidden />
                            Featured
                          </span>
                        ) : (
                          <OutlineButton
                            type="button"
                            disabled={isFeaturingIteration}
                            onClick={() => {
                              setFeaturedConfirmRow({
                                _rowKey: `lead-iter-${selectedIterBrowseId}`,
                                projectPhaseId: browseProjectPhaseId,
                                designIterationId: selectedIterBrowseId,
                                idLead: lead,
                                gridTitle: selectedSubmission.title || "Design",
                                title: selectedSubmission.title || "Design",
                              });
                            }}
                            className="text-sm px-3 py-1.5 whitespace-nowrap"
                          >
                            {isFeaturingIteration ? (
                              <span className="inline-flex items-center justify-center gap-2">
                                <span
                                  className="w-4 h-4 border-2 border-flamingo-500 border-t-transparent rounded-full animate-spin shrink-0"
                                  aria-hidden
                                />
                                Saving…
                              </span>
                            ) : (
                              "Feature design"
                            )}
                          </OutlineButton>
                        ))}
                      {(() => {
                        // Lock out further work once the buyer has approved
                        // or the lead has moved to developer review/approval.
                        // Once an iteration is approved (by buyer or dev), the
                        // designer shouldn't be able to push new iterations or
                        // edit the BOQ — the design is settled.
                        const itStatus = (selectedIteration?.iterationStatus ?? selectedIteration?.iteration_status ?? '').toString().toUpperCase();
                        const devStatus = (selectedIteration?.developerApprovalStatus ?? selectedIteration?.developer_approval_status ?? '').toString().toUpperCase();
                        const iterApproved = itStatus === 'APPROVED' || devStatus === 'DEVELOPER_APPROVED';
                        const leadDecided =
                          hasStatus(lead, 'BUYER_DESIGN_APPROVED') ||
                          hasStatus(lead, 'DEVELOPER_APPROVED');
                        const lockedForApproval = iterApproved || leadDecided;
                        const pendingAck = Boolean(selectedIteration.buyerFeedback) && !selectedIteration.isIdAcknowledged;
                        const disableBecausePending = pendingAck || lockedForApproval;
                        // BOQ is entered only on the FINAL iteration (the modal and backend
                        // enforce the same rule) — don't even offer "Edit BOQ" otherwise.
                        const iterationIsFinal = Boolean(selectedIteration?.isFinal ?? selectedIteration?.is_final);
                        const lockTitle = lockedForApproval
                          ? "This design has been approved — no further changes can be uploaded."
                          : pendingAck
                            ? "Acknowledge buyer feedback before uploading again."
                            : undefined;
                        return (
                          <>
                            {iterationIsFinal && (
                              <OutlineButton
                                onClick={openEditBoq}
                                disabled={disableBecausePending}
                                title={lockTitle}
                                className="text-sm px-3 py-1.5 whitespace-nowrap"
                              >
                                Edit BOQ
                              </OutlineButton>
                            )}
                            <PrimaryButton
                              onClick={openUploadUpdatedDesign}
                              disabled={disableBecausePending}
                              title={lockTitle}
                              className="text-sm px-3 py-1.5 whitespace-nowrap inline-flex items-center gap-1.5"
                            >
                              <Upload size={14} aria-hidden />
                              Upload Design
                            </PrimaryButton>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  </div>{/* /iteration gradient wrapper */}
                </div>{/* /iteration outer card */}

                {/* Content – Design Images carousel + Notes side by side */}
                <div className="p-3 lg:p-4">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Design Images Carousel – takes 2 cols on large screens */}
                    <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                      <h2 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em] mb-3 flex items-center gap-1.5">
                        <ImageIcon size={13} className="text-flamingo-600" />
                        Design Images
                      </h2>
                      {selectedIteration.media ? (
                        (() => {
                          const bySection = normalizeMediaBySection(selectedIteration.media);
                          const sectionOrder = getSectionDisplayOrder();
                          const sectionsWithMedia = sectionOrder.filter((sec) => bySection[sec]?.length > 0);
                          const uncategorizedKeys = Object.keys(bySection).filter((k) => !sectionOrder.includes(k));
                          const allSectionKeys = [...sectionsWithMedia, ...uncategorizedKeys];
                          const allItems = allSectionKeys.flatMap((sec) =>
                            (bySection[sec] || []).map((item) => ({ ...item, section: sec }))
                          );
                          if (allItems.length === 0) {
                            return (
                              <div className="flex items-center justify-center py-12 text-gray-400 border border-dashed border-gray-200 rounded-lg">
                                <ImageIcon className="mr-2" size={20} />
                                <span className="text-sm">No images</span>
                              </div>
                            );
                          }
                          const current = allItems[designCarouselIndex];
                          const imageSrc = getImageSrc(current);
                          return (
                            <div className="relative flex justify-center items-center p-4">
                              {allItems.length > 1 && (
                                <>
                                  <div className="absolute -translate-x-16 lg:-translate-x-20 scale-90 opacity-20 z-0 pointer-events-none">
                                    <LoadingImage
                                      src={getImageSrc(allItems[(designCarouselIndex - 1 + allItems.length) % allItems.length])}
                                      alt="Previous"
                                      containerClassName="w-32 h-32 lg:w-40 lg:h-40 rounded-lg"
                                      className="w-32 h-32 lg:w-40 lg:h-40 object-cover rounded-lg"
                                    />
                                  </div>
                                  <div className="absolute translate-x-16 lg:translate-x-20 scale-90 opacity-20 z-0 pointer-events-none">
                                    <LoadingImage
                                      src={getImageSrc(allItems[(designCarouselIndex + 1) % allItems.length])}
                                      alt="Next"
                                      containerClassName="w-32 h-32 lg:w-40 lg:h-40 rounded-lg"
                                      className="w-32 h-32 lg:w-40 lg:h-40 object-cover rounded-lg"
                                    />
                                  </div>
                                </>
                              )}
                              <div className="relative z-10">
                                {imageSrc ? (
                                  <a href={imageSrc} target="_blank" rel="noopener noreferrer" className="block relative w-full max-w-[200px] lg:max-w-[280px] aspect-square mx-auto rounded-lg overflow-hidden">
                                    <LoadingImage
                                      src={imageSrc}
                                      alt={current?.section ? `${current.section} ${designCarouselIndex + 1}` : `Design ${designCarouselIndex + 1}`}
                                      containerClassName="w-full h-full rounded-lg"
                                      className="w-full h-full object-cover"
                                      fallback={
                                        <div className="absolute inset-0 flex flex-col items-center justify-center p-3 bg-gray-50 rounded-lg">
                                          <FileText className="text-gray-400" size={24} />
                                          <p className="text-sm text-gray-600 truncate">{current?.section || "Design"}</p>
                                        </div>
                                      }
                                    />
                                  </a>
                                ) : (
                                  <div className="w-40 h-40 flex flex-col items-center justify-center rounded-lg bg-gray-50 border border-gray-200">
                                    <FileText className="text-gray-400" size={24} />
                                    <p className="text-sm text-gray-600 mt-1">{current?.section || "Design"}</p>
                                  </div>
                                )}
                              </div>
                              {allItems.length > 1 && (
                                <>
                                  <div className="absolute top-1/2 -translate-y-1/2 flex justify-between w-full px-1 z-20">
                                    <button
                                      onClick={() => setDesignCarouselIndex((i) => (i > 0 ? i - 1 : allItems.length - 1))}
                                      className="bg-white/90 hover:bg-white rounded-full p-1.5 shadow-lg text-gray-600 hover:text-gray-900"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => setDesignCarouselIndex((i) => (i < allItems.length - 1 ? i + 1 : 0))}
                                      className="bg-white/90 hover:bg-white rounded-full p-1.5 shadow-lg text-gray-600 hover:text-gray-900"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                      </svg>
                                    </button>
                                  </div>
                                  <p className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-sm text-gray-500 tabular-nums whitespace-nowrap">
                                    {designCarouselIndex + 1} / {allItems.length}
                                    {current?.section && <span className="ml-1.5 text-gray-400">· {current.section}</span>}
                                  </p>
                                </>
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        <div className="flex items-center justify-center py-12 text-gray-400 border border-dashed border-gray-200 rounded-lg">
                          <ImageIcon className="mr-2" size={16} />
                          <span className="text-sm">No images for this iteration</span>
                        </div>
                      )}
                    </div>

                    {/* Conversation thread — BV RequestDetails parity. Slate
                        "Designer notes" bubble on the left, flamingo "Buyer
                        feedback" bubble on the right. */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                      <div className="px-4 py-3 border-b border-slate-100">
                        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                          <MessageCircle size={16} className="text-flamingo-600" />
                          Conversation
                        </h3>
                        <p className="mt-0.5 text-xs text-slate-500">Designer notes &amp; buyer feedback for this iteration.</p>
                      </div>
                      <div className="p-4 space-y-3">
                        {/* Designer notes bubble (left, slate) */}
                        {(selectedIteration.designerNotes || selectedSubmission.description) && (
                          <div className="flex items-start gap-2">
                            <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 ring-1 ring-slate-200">
                              <Palette size={13} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                                {selectedIteration.designerNotes ? "Designer notes" : "Description"}
                              </p>
                              <div
                                className="mt-1 rounded-2xl rounded-tl-sm bg-slate-50 p-3 text-sm text-slate-700 leading-relaxed cursor-pointer hover:bg-slate-100 transition-colors"
                                onClick={() =>
                                  openNotesModal(
                                    selectedIteration.designerNotes || selectedSubmission.description,
                                    selectedIteration.designerNotes ? "Designer Notes" : "Description"
                                  )
                                }
                                title="Click to view full comment"
                              >
                                <p className="line-clamp-5 whitespace-pre-wrap">
                                  {selectedIteration.designerNotes || selectedSubmission.description}
                                </p>
                              </div>
                              {selectedIteration.createdDate && (
                                <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-400">
                                  <Clock size={10} />
                                  {formatDate(selectedIteration.createdDate)}
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Buyer feedback bubble (right, flamingo) */}
                        {selectedIteration.buyerFeedback && (
                          <div className="flex items-start gap-2 flex-row-reverse">
                            <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-flamingo-100 text-flamingo-600 ring-1 ring-flamingo-200">
                              <Home size={13} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[11px] font-medium uppercase tracking-wide text-flamingo-600 text-right">
                                Buyer feedback
                              </p>
                              <div className="mt-1 rounded-2xl rounded-tr-sm bg-flamingo-50 p-3 text-sm text-slate-800 leading-relaxed border border-flamingo-100">
                                <p className="whitespace-pre-wrap">{selectedIteration.buyerFeedback}</p>
                              </div>
                              {selectedIteration.feedbackDate && (
                                <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-400 justify-end">
                                  <Clock size={10} />
                                  {formatDateTime(selectedIteration.feedbackDate)}
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Buyer feedback media (right, flamingo) */}
                        {(() => {
                          const feedbackMedia = selectedIteration?.buyerFeedbackMedia ?? selectedIteration?.buyer_feedback_media;
                          const mediaList = Array.isArray(feedbackMedia) ? feedbackMedia : [];
                          if (mediaList.length === 0) return null;
                          return (
                            <div className="flex items-start gap-2 flex-row-reverse">
                              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-flamingo-100 text-flamingo-600 ring-1 ring-flamingo-200">
                                <ImageIcon size={13} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-[11px] font-medium uppercase tracking-wide text-flamingo-600 text-right">
                                  Feedback images
                                </p>
                                <div className="mt-1 flex flex-wrap gap-2 justify-end">
                                  {mediaList.map((item, idx) => {
                                    const url = item?.url ?? item?.downloadUrl;
                                    const caption = item?.caption ?? "";
                                    if (!url) return null;
                                    return (
                                      <div key={idx} className="flex flex-col items-end">
                                        <a href={url} target="_blank" rel="noopener noreferrer" className="block rounded-xl overflow-hidden border border-flamingo-100 hover:border-flamingo-300 w-20 h-20">
                                          <LoadingImage src={url} alt={caption || `Feedback ${idx + 1}`} containerClassName="w-full h-full" className="w-full h-full object-cover" />
                                        </a>
                                        {caption && <p className="text-[10px] text-slate-500 mt-0.5 max-w-20 truncate">{caption}</p>}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {!selectedIteration.buyerFeedback && !(Array.isArray(selectedIteration?.buyerFeedbackMedia) && selectedIteration.buyerFeedbackMedia.length > 0) && !(Array.isArray(selectedIteration?.buyer_feedback_media) && selectedIteration.buyer_feedback_media.length > 0) && !selectedIteration.designerNotes && !selectedSubmission?.description && (
                          <p className="text-sm text-slate-400 py-4 text-center">No activity yet</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Iterations tab: no request selected – show empty state */
              <div className="flex items-center justify-center min-h-[40vh] p-4 lg:p-6">
                <div className="text-center max-w-md">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1.5">Requests</h3>
                  <p className="text-gray-500 text-sm">
                    {requests.length > 0
                      ? "Select a request from the inbox to view design details and buyer feedback."
                      : "Requests will appear here after you submit a design. Use Design submission to create your first submission."}
                  </p>
                </div>
              </div>
            )}
                  </>
              )}
            </div>
          </div>
        </div>
      </div>

      {Boolean(notesModalContent) && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 font-['Gotham',sans-serif] font-light antialiased"
          onClick={() => setNotesModalContent("")}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">{notesModalTitle}</h3>
              <CloseButton
                onClick={() => setNotesModalContent("")}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                aria-label="Close"
              >
                <X size={20} />
              </CloseButton>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {notesModalContent}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Requirements modal - renders HTML content */}
      {showRequirementsModal && lead?.requirements && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 font-['Gotham',sans-serif] font-light antialiased"
          onClick={() => setShowRequirementsModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 shrink-0">
              <div className="flex items-center gap-2">
                <FileText className="text-gray-500" size={20} />
                <h3 className="text-lg font-semibold text-gray-900">Requirements</h3>
              </div>
              <CloseButton
                onClick={() => setShowRequirementsModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                aria-label="Close"
              >
                <X size={20} />
              </CloseButton>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <style>{`
                .requirements-content { font-family: "Gotham", sans-serif; font-weight: 300; }
                .requirements-content h1, .requirements-content h2 { font-size: 1.125rem; font-weight: 600; color: #111827; margin: 1rem 0 0.5rem; }
                .requirements-content h3 { font-size: 1rem; font-weight: 600; color: #111827; margin: 0.75rem 0 0.5rem; }
                .requirements-content p { margin: 0.5rem 0; color: #374151; line-height: 1.6; font-size: 0.875rem; }
                .requirements-content ul { margin: 0.5rem 0; padding-left: 1.5rem; color: #374151; }
                .requirements-content li { margin: 0.25rem 0; line-height: 1.5; font-size: 0.875rem; }
                .requirements-content strong { font-weight: 600; color: #111827; }
                .requirements-content u { text-decoration: underline; }
                .requirements-content blockquote { border-left: 4px solid #e5e7eb; padding-left: 1rem; margin: 0.5rem 0; color: #6b7280; font-style: italic; }
              `}</style>
              <div
                className="requirements-content"
                dangerouslySetInnerHTML={{ __html: lead.requirements }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Accept confirmation modal */}
      {showAcceptConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 font-['Gotham',sans-serif] font-light antialiased">
          <AmberReviewRequestPanel
            className="max-w-md w-full shadow-xl"
            bodyVariant="column"
            title="Confirm accept"
            description="Are you sure you want to accept this feedback? You can upload an updated design or edit the BOQ after accepting."
            actions={
              <>
                <OutlineButton
                  onClick={() => setShowAcceptConfirmModal(false)}
                  disabled={acknowledging}
                  className="px-4 py-2"
                >
                  Cancel
                </OutlineButton>
                <PrimaryButton
                  onClick={() => submitAcknowledge("accept")}
                  disabled={acknowledging}
                  className="px-4 py-2 flex items-center gap-2"
                >
                  {acknowledging ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Check size={16} />
                  )}
                  Confirm accept
                </PrimaryButton>
              </>
            }
          />
        </div>
      )}

      {/* Decline modal – requires description as per endpoint */}
      {showDeclineModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 font-['Gotham',sans-serif] font-light antialiased">
          <AmberReviewRequestPanel
            className="max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl"
            bodyVariant="column"
            title="Decline feedback"
            description="Please provide a reason for declining. This will be recorded with the request."
            actions={
              <>
                <OutlineButton
                  onClick={() => {
                    setShowDeclineModal(false);
                    setDeclineNote("");
                  }}
                  disabled={acknowledging}
                  className="px-4 py-2"
                >
                  Cancel
                </OutlineButton>
                <PrimaryButton
                  onClick={() => submitAcknowledge("reject", declineNote)}
                  disabled={acknowledging || !declineNote.trim()}
                  className="px-4 py-2 flex items-center gap-2 border-red-600 bg-red-600 hover:bg-red-700"
                >
                  {acknowledging ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <XCircle size={16} />
                  )}
                  Decline
                </PrimaryButton>
              </>
            }
          >
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={declineNote}
                onChange={(e) => setDeclineNote(e.target.value)}
                placeholder="Enter reason for declining..."
                rows={4}
                className="w-full px-3 py-2 border border-amber-200/80 rounded-lg text-sm bg-white/90 focus:outline-none focus:ring-2 focus:ring-flamingo-500 focus:border-flamingo-500 disabled:opacity-50"
                disabled={acknowledging}
              />
            </div>
          </AmberReviewRequestPanel>
        </div>
      )}

      {featuredConfirmRow && (
        <ConfirmationModal
          isOpen
          title="Feature for browse?"
          onConfirm={handleFeaturedConfirmSubmit}
          onCancel={handleFeaturedConfirmClose}
          cancelButtonText="Cancel"
          confirmButtonText="Make featured"
          loadingText="Featuring…"
          isLoading={isFeaturingIteration}
          panelClassName="max-w-lg"
        >
          <FeaturedBrowseConfirmDetails row={featuredConfirmRow} />
        </ConfirmationModal>
      )}

      {/* Design Submission Modal */}
      {showDesignModal && lead && (() => {
        // If we're creating a new iteration, carry isFinal from any previous iteration in this submission
        const submissionIdForIteration = iterationUploadSubmission?.designSubmissionId ?? iterationUploadSubmission?.id ?? selectedSubmission?.designSubmissionId ?? selectedSubmission?.id;
        const wrapper = submissionIdForIteration && designSubmissions?.length
          ? designSubmissions.find((sw) => {
              const sub = sw?.designSubmission || sw;
              const id = sub?.designSubmissionId ?? sub?.id;
              return id != null && parseInt(id) === parseInt(submissionIdForIteration);
            })
          : null;
        const iterationsForSubmission = wrapper?.designIterations ?? [];
        const anyIterationIsFinal = iterationsForSubmission.some((it) => it.isFinal === true);
        // Use price from first iteration (original design submission) for new iterations
        const sortedByIteration = [...iterationsForSubmission].sort(
          (a, b) => (a.iterationNumber ?? 1) - (b.iterationNumber ?? 1)
        );
        const originalDesignPrice = sortedByIteration[0]?.estimatedPrice ?? selectedIteration?.estimatedPrice;

        return (
        <DesignSubmissionModal
          isOpen={showDesignModal}
          onClose={() => {
            setShowDesignModal(false);
            setIterationUploadSubmission(null);
            setDesignModalStep(0);
          }}
          configId={lead.property?.propertyConfig?.propertyConfigId}
          leadId={lead.interiorDesignerLeadId}
          lead={lead}
          /* portfolioId is intentionally NOT passed in the lead/iteration
             flow. BOQ scopes are mutually exclusive — design-iteration BOQ
             uses `designIterationId`, portfolio BOQ uses `portfolioId`.
             Passing both here caused "BOQ has both design iteration AND
             portfolio scope" because BoqContainer's validator rejects the
             dual-scope. Portfolio BOQ has its own entry point elsewhere. */
          portfolioId={null}
          iterationSubmission={iterationUploadSubmission}
          editingDesign={designModalStep === 1 ? selectedSubmission : null}
          editingIterationId={designModalStep === 1 ? (selectedIterationId ?? selectedIteration?.designIterationId ?? selectedIteration?.id) : null}
          startAtStep={designModalStep}
          isFinalForNewIteration={anyIterationIsFinal}
          previousEstimatedPrice={originalDesignPrice}
          onSuccess={() => {
            setShowDesignModal(false);
            const selectSubmissionId = iterationUploadSubmission?.designSubmissionId || (designModalStep === 1 ? selectedSubmission?.designSubmissionId : null);
            setIterationUploadSubmission(null);
            setDesignModalStep(0);
            setSelectedIterationId(null);
            setSelectedIterationData(null);
            setSelectedSubmissionData(null);
            // Refresh the iteration list AND the lead — submitting a design
            // advances lead status (ID_DESIGN_SUBMITTED) and clears deadlines.
            fetchDesignSubmissions(lead.interiorDesignerLeadId, {
              selectLatestForSubmissionId: selectSubmissionId,
            });
            refreshLeadFromApi();
          }}
        />
        );
      })()}
    </>
  );
};

export default LeadDetails;
